import { ReactText } from "react";
import {
	KeyValuePair,
	ZomentumEntities,
	Address,
	AddressValues
} from "../common";
import { EmailThreadParticipant } from "../Others/EmailThread";
import { UserData } from "../Users";
import { Pipeline } from "../Pipelines";
import {
	Distributor,
	ProductType,
	SelectedCustomDistributorDetails
} from "../Products";
import { DocumentStatus, Gateway } from "../Documents";
import { DocumentApproval } from "../DocumentApprovals";
import { Report } from "../Reports/Reports.contract";
import { SalesActivityAutomation } from "../Others/SalesActivity";
import moment from "moment";

export enum IntegrationProviders {
	ConnectWise = "connectwise",
	Email = "email",
	Autotask = "autotask"
}

export enum SyncSource {
	ConnectWise = "connectwise",
	Autotask = "autotask",
	Zapier = "zapier",
	Kaseya = "kaseya",
	Syncro = "syncro",
	InfusionSoft = "infusion_soft",
	Hubspot = "hubspot",
	RepairShopr = "repairshopr",
	Halo = "halo",
	Airtable = "airtable",
	SuperOps = "superops"
}

export const PSAList: { label: string; value: SyncSource }[] = [
	{ label: "Autotask", value: SyncSource.Autotask },
	{ label: "ConnectWise PSA", value: SyncSource.ConnectWise },
	{ label: "Kaseya BMS", value: SyncSource.Kaseya },
	{ label: "Syncro", value: SyncSource.Syncro },
	{ label: "RepairShopr", value: SyncSource.RepairShopr },
	{ label: "Halo", value: SyncSource.Halo }
];

export interface EmailAliasFormValues {
	name: string;
	email: string;
}

export const timezones = [
	{
		name: "UTC",
		offset: 0
	},
	{
		name: "UTC +00:00 Canary, Faeroe, Faroe, and Madeira Time",
		offset: 0
	},
	{
		name: "UTC +00:00 Danmarkshavn Time",
		offset: 0
	},
	{
		name: "UTC +00:00 Dublin, London, and Lisbon Time",
		offset: 0
	},
	{
		name: "UTC +00:00 Greenwich Mean Time",
		offset: 0
	},
	{
		name: "UTC +00:00 Reykjavik and St Helena Time",
		offset: 0
	},
	{
		name: "UTC +00:00 Troll Time",
		offset: 0
	},
	{
		name: "UTC +01:00 Casablanca and El Aaiun Time",
		offset: -60
	},
	{
		name: "UTC +01:00 Central European Time",
		offset: -60
	},
	{
		name: "UTC +01:00 Ceuta Time",
		offset: -60
	},
	{
		name: "UTC +01:00 Jan Mayen Time",
		offset: -60
	},
	{
		name: "UTC +01:00 Longyearbyen Time",
		offset: -60
	},
	{
		name: "UTC +02:00 Amman Time",
		offset: -120
	},
	{
		name: "UTC +02:00 Beirut Time",
		offset: -120
	},
	{
		name: "UTC +02:00 Chisinau and Tiraspol Time",
		offset: -120
	},
	{
		name: "UTC +02:00 Damascus Time",
		offset: -120
	},
	{
		name: "UTC +02:00 Eastern European Time",
		offset: -120
	},
	{
		name: "UTC +02:00 Famagusta and Nicosia Time",
		offset: -120
	},
	{
		name: "UTC +02:00 Gaza and Hebron Time",
		offset: -120
	},
	{
		name: "UTC +02:00 Jerusalem and Tel Aviv Time",
		offset: -120
	},
	{
		name: "UTC +02:00 Kaliningrad Time",
		offset: -120
	},
	{
		name: "UTC +02:00 South Africa Time",
		offset: -120
	},
	{
		name: "UTC +03:00 Aden, Baghdad, Bahrain, Istanbul, Kuwait, Qatar, and Riyadh Time",
		offset: -180
	},
	{
		name: "UTC +03:00 Antananarivo, Comoro, and Mayotte Time",
		offset: -180
	},
	{
		name: "UTC +03:00 East Africa Time",
		offset: -180
	},
	{
		name: "UTC +03:00 Istanbul, Kirov, Minsk, Moscow, and Simferopol Time",
		offset: -180
	},
	{
		name: "UTC +03:00 Syowa Time",
		offset: -180
	},
	{
		name: "UTC +03:30 Tehran Time",
		offset: -210
	},
	{
		name: "UTC +04:00 Astrakhan, Samara, Saratov, Ulyanovsk, and Volgograd Time",
		offset: -240
	},
	{
		name: "UTC +04:00 Baku, Dubai, Muscat, Tbilisi, and Yerevan Time",
		offset: -240
	},
	{
		name: "UTC +04:00 Mahe, Mauritius, and Reunion Time",
		offset: -240
	},
	{
		name: "UTC +04:30 Kabul Time",
		offset: -270
	},
	{
		name: "UTC +05:00 Aqtau, Aqtobe, Ashgabat, Ashkhabad, Atyrau, Dushanbe, Karachi, Oral, Qyzylorda, Samarkand, Tashkent, and Yekaterinburg Time",
		offset: -300
	},
	{
		name: "UTC +05:00 Kerguelen and Maldives Time",
		offset: -300
	},
	{
		name: "UTC +05:00 Mawson Time",
		offset: -300
	},
	{
		name: "UTC +05:30 New Delhi, Mumbai, and Calcutta Time",
		offset: -330
	},
	{
		name: "UTC +05:45 Kathmandu and Katmandu Time",
		offset: -345
	},
	{
		name: "UTC +06:00 Almaty, Bishkek, Dacca, Dhaka, Kashgar, Omsk, Kostanay, Thimbu, Thimphu, and Urumqi Time",
		offset: -360
	},
	{
		name: "UTC +06:00 Chagos Time",
		offset: -360
	},
	{
		name: "UTC +06:00 Vostok Time",
		offset: -360
	},
	{
		name: "UTC +06:30 Cocos Time",
		offset: -390
	},
	{
		name: "UTC +06:30 Rangoon and Yangon Time",
		offset: -390
	},
	{
		name: "UTC +07:00 Christmas Time",
		offset: -420
	},
	{
		name: "UTC +07:00 Davis Time",
		offset: -420
	},
	{
		name: "UTC +07:00 Indochina Time",
		offset: -420
	},
	{
		name: "UTC +08:00 Casey Time",
		offset: -480
	},
	{
		name: "UTC +08:00 China, Hong Kong, and Singapore Time",
		offset: -480
	},
	{
		name: "UTC +08:00 Perth and West Time",
		offset: -480
	},
	{
		name: "UTC +08:45 Eucla Time",
		offset: -525
	},
	{
		name: "UTC +09:00 Palau Time",
		offset: -540
	},
	{
		name: "UTC +09:00 Tokyo, Seoul, Chita, Dili, Jayapura, Khandyga, Pyongyang, and Yakutsk Time",
		offset: -540
	},
	{
		name: "UTC +09:30 Darwin and North Time",
		offset: -570
	},
	{
		name: "UTC +10:00 Brisbane, Lindeman, and Queensland Time",
		offset: -600
	},
	{
		name: "UTC +10:00 Chuuk, Guam, Port Moresby, Saipan, Truk, and Yap Time",
		offset: -600
	},
	{
		name: "UTC +10:00 DumontDUrville Time",
		offset: -600
	},
	{
		name: "UTC +10:00 Ust-Nera and Vladivostok Time",
		offset: -600
	},
	{
		name: "UTC +10:30 Adelaide, Broken Hill, South, and Yancowinna Time",
		offset: -630
	},
	{
		name: "UTC +11:00 Australian Eastern Daylight Time",
		offset: -660
	},
	{
		name: "UTC +11:00 Bougainville, Efate, Guadalcanal, Kosrae, Norfolk, Noumea, Pohnpei, and Ponape Time",
		offset: -660
	},
	{
		name: "UTC +11:00 LHI and Lord Howe Time",
		offset: -660
	},
	{
		name: "UTC +11:00 Macquarie Time",
		offset: -660
	},
	{
		name: "UTC +11:00 Magadan, Sakhalin, and Srednekolymsk Time",
		offset: -660
	},
	{
		name: "UTC +12:00 Anadyr and Kamchatka Time",
		offset: -720
	},
	{
		name: "UTC +12:00 Funafuti, Kwajalein, Majuro, Nauru, Tarawa, Wake, and Wallis Time",
		offset: -720
	},
	{
		name: "UTC +13:00 Auckland Time",
		offset: -780
	},
	{
		name: "UTC +13:00 Enderbury, Fakaofo, and Tongatapu Time",
		offset: -780
	},
	{
		name: "UTC +13:00 Fiji Time",
		offset: -780
	},
	{
		name: "UTC +13:00 McMurdo and South Pole Time",
		offset: -780
	},
	{
		name: "UTC +13:45 Chatham Time",
		offset: -825
	},
	{
		name: "UTC +14:00 Apia Time",
		offset: -840
	},
	{
		name: "UTC +14:00 Kiritimati Time",
		offset: -840
	},
	{
		name: "UTC -01:00 Azores Time",
		offset: 60
	},
	{
		name: "UTC -01:00 Cape Verde Time",
		offset: 60
	},
	{
		name: "UTC -01:00 Scoresbysund Time",
		offset: 60
	},
	{
		name: "UTC -02:00 DeNoronha Time",
		offset: 120
	},
	{
		name: "UTC -02:00 Noronha Time",
		offset: 120
	},
	{
		name: "UTC -02:00 South Georgia Time",
		offset: 120
	},
	{
		name: "UTC -03:00 Argentinian Time",
		offset: 180
	},
	{
		name: "UTC -03:00 Asuncion Time",
		offset: 180
	},
	{
		name: "UTC -03:00 Continental Time",
		offset: 180
	},
	{
		name: "UTC -03:00 East Time",
		offset: 180
	},
	{
		name: "UTC -03:00 Godthab Time",
		offset: 180
	},
	{
		name: "UTC -03:00 Miquelon Time",
		offset: 180
	},
	{
		name: "UTC -03:00 Palmer and Rothera Time",
		offset: 180
	},
	{
		name: "UTC -03:00 Santiago Time",
		offset: 180
	},
	{
		name: "UTC -03:30 Newfoundland Time",
		offset: 150
	},
	{
		name: "UTC -03:30 St Johns Time",
		offset: 150
	},
	{
		name: "UTC -04:00 Atlantic Time",
		offset: 240
	},
	{
		name: "UTC -04:00 Bermuda Time",
		offset: 240
	},
	{
		name: "UTC -04:00 Eastern Carribean Time",
		offset: 240
	},
	{
		name: "UTC -04:00 Glace Bay, Goose Bay, Halifax, Moncton, and Thule Time",
		offset: 240
	},
	{
		name: "UTC -04:00 West Time",
		offset: 240
	},
	{
		name: "UTC -05:00 Acre Time",
		offset: 300
	},
	{
		name: "UTC -05:00 Easter Time",
		offset: 300
	},
	{
		name: "UTC -05:00 EasterIsland Time",
		offset: 300
	},
	{
		name: "UTC -05:00 Eastern Time",
		offset: 300
	},
	{
		name: "UTC -05:00 Havana Time",
		offset: 300
	},
	{
		name: "UTC -05:00 Jamaica and Panama Time",
		offset: 300
	},
	{
		name: "UTC -06:00 Bahia Banderas, Merida, Mexico City, and Monterrey Time",
		offset: 360
	},
	{
		name: "UTC -06:00 Belize, Costa Rica, El Salvador, Guatemala, Managua, Regina, Swift Current, and Tegucigalpa Time",
		offset: 360
	},
	{
		name: "UTC -06:00 Central Time",
		offset: 360
	},
	{
		name: "UTC -06:00 Galapagos Time",
		offset: 360
	},
	{
		name: "UTC -06:00 General Time",
		offset: 360
	},
	{
		name: "UTC -06:00 Saskatchewan Time",
		offset: 360
	},
	{
		name: "UTC -07:00 Arizona Time",
		offset: 420
	},
	{
		name: "UTC -07:00 BajaSur Time",
		offset: 420
	},
	{
		name: "UTC -07:00 Chihuahua and Mazatlan Time",
		offset: 420
	},
	{
		name: "UTC -07:00 Mountain Time",
		offset: 420
	},
	{
		name: "UTC -08:00 BajaNorte Time",
		offset: 480
	},
	{
		name: "UTC -08:00 Pacific Time",
		offset: 480
	},
	{
		name: "UTC -08:00 Pacific and Yukon Time",
		offset: 480
	},
	{
		name: "UTC -08:00 Pitcairn Time",
		offset: 480
	},
	{
		name: "UTC -09:00 Anchorage, Juneau, Metlakatla, Nome, Sitka, and Yakutat Time",
		offset: 540
	},
	{
		name: "UTC -09:00 Gambier Time",
		offset: 540
	},
	{
		name: "UTC -09:30 Marquesas Time",
		offset: 510
	},
	{
		name: "UTC -10:00 Adak and Atka Time",
		offset: 600
	},
	{
		name: "UTC -10:00 Honolulu, Johnston, Rarotonga, and Tahiti Time",
		offset: 600
	},
	{
		name: "UTC -11:00 Midway, Niue, Pago Pago, and Samoa Time",
		offset: 660
	}
];

export enum AutotaskFilterDataType {
	Checkbox = "checkbox",
	Dropdown = "dropdown",
	Datepicker = "date",
	InputText = "text",
	InputNumber = "number"
}

export interface AutotaskFilterOptionValue {
	label: string;
	value: string;
}

export interface AutotaskSyncAdvancedOption {
	readonly data_type: AutotaskFilterDataType;
	readonly entity: string;
	readonly field: string;
	is_enabled: boolean;
	readonly is_multi_select: boolean;
	readonly label: string;
	readonly allowed_values: AutotaskFilterOptionValue[];
	readonly allowed_modes?: AutotaskSyncProperty[];
	values: AutotaskFilterOptionValue[];
}

export enum AutotaskSyncProperty {
	Push = "push",
	Pull = "pull",
	Pull_Always_Push_On_Won = "push_n_pull",
	PushPullAlways = "push_n_pull_realtime"
}

export interface AutotaskSyncOption {
	readonly data_type: AutotaskFilterDataType;
	readonly entity: string;
	readonly is_enabled: boolean;
	readonly label: string;
	readonly advanced_options: AutotaskSyncAdvancedOption[];
	readonly allowed_modes: AutotaskSyncProperty[];
}

export interface AutotaskExistingFieldMapping {
	readonly autotask_value: string;
	readonly zomentum_value: string;
	entity: string;
	field: string;
}

export interface AutotaskMappingField {
	readonly can_edit: boolean;
	mapping: AutotaskExistingFieldMapping[];
	default_autotask_value: string;
	readonly zomentum_label: string;
	readonly autotask_label: string;
	readonly field: string;
	readonly autotask_values: AutotaskFilterOptionValue[];
	readonly zomentum_values: AutotaskFilterOptionValue[];
}

export interface AutotaskExisitingCustomFieldMapping {
	zomentum_field: string;
	autotask_field: string;
}

export interface AutotaskCustomFieldMappingRequest {
	zomentum_custom_field: string;
	autotask_custom_field: string;
}

export interface AutotaskCustomField {
	data_type: AutotaskCustomFieldType;
	is_dropdown: boolean;
	name: string;
	value: string;
}

export interface ZomentumCustomFieldForAutotaskMapping {
	data_type: CustomFieldType;
	id: string;
	name: string;
}

export interface AutotaskMappingCustomField {
	autotask_label: string;
	zomentum_label: string;
	mapping: AutotaskExisitingCustomFieldMapping[];
	autotask_values: AutotaskCustomField[];
	zomentum_values: ZomentumCustomFieldForAutotaskMapping[];
}

export interface AutotaskMapping {
	readonly entity: string;
	readonly fields: AutotaskMappingField[];
}

export enum IntegrationMode {
	CPQ = "cpq",
	CRM = "crm"
}

export interface AutotaskDefaultOption {
	readonly entity: string;
	readonly field: string;
	readonly label: string;
	readonly default_autotask_value: string | null;
	readonly autotask_values: AutotaskFilterOptionValue[];
	readonly allowed_modes: AutotaskSyncProperty[];
	readonly data_type: AutotaskFilterDataType;
}

export interface AutotaskCustomFieldMappingsFormValues {
	client_company_field_mapping: Record<string, string>;
	contact_field_mapping: Record<string, string>;
	opportunity_field_mapping: Record<string, string>;
	pricing_field_mapping: Record<string, string>;
}

export const generateCustomFieldMappingRequest = (
	mapping: Record<string, string>
): AutotaskCustomFieldMappingRequest[] => {
	const customFieldMapping: AutotaskCustomFieldMappingRequest[] = [];
	Object.keys(mapping).forEach(key => {
		const autotaskCustomField = mapping[key]?.split(".")?.[1];
		if (!autotaskCustomField) return;
		customFieldMapping.push({
			zomentum_custom_field: key,
			autotask_custom_field: autotaskCustomField
		});
	});
	return customFieldMapping;
};

export interface AutotaskIntegrationResponse {
	readonly username: string | null;
	readonly password: string | null;
	readonly sync_property?: AutotaskSyncProperty;
	readonly sync_options: AutotaskSyncOption[];
	readonly mapping: AutotaskMapping[];
	readonly mode: IntegrationMode;
	readonly status_as_custom_field_enabled?: boolean;
	readonly pull_closed_opportunity_to_won_stage?: boolean;
	readonly pull_lost_opportunity_to_lost_stage?: boolean;
	readonly is_tax_imported_from_autotask?: boolean;
	readonly integrated_pipeline_id?: string;
	readonly default_options?: AutotaskDefaultOption[];
	readonly update_status_to_closed_if_won?: boolean;
	readonly update_status_to_lost_if_lost?: boolean;
	readonly client_company_field_mapping?:
		| AutotaskMappingCustomField
		| AutotaskCustomFieldMappingRequest[];
	readonly contact_field_mapping?:
		| AutotaskMappingCustomField
		| AutotaskCustomFieldMappingRequest[];
	readonly opportunity_field_mapping?:
		| AutotaskMappingCustomField
		| AutotaskCustomFieldMappingRequest[];
	readonly pricing_field_mapping?:
		| AutotaskMappingCustomField
		| AutotaskCustomFieldMappingRequest[]
		| null;
	readonly custom_field_mapping?: AutotaskCustomFieldMappingsFormValues;
}

export enum ConnectwiseCustomFieldType {
	Button = "Button",
	Checkbox = "Checkbox",
	Date = "Date",
	Hyperlink = "Hyperlink",
	Number = "Number",
	Percent = "Percent",
	Text = "Text",
	TextArea = "TextArea"
}

export enum ConnectwiseSystemFieldType {
	Number = "cw_number_field",
	Double = "cw_double_field",
	Text = "cw_text_field",
	Dropdown = "cw_dropdown_field"
}

export enum AutotaskCustomFieldType {
	Decimal = "decimal",
	Double = "double",
	Long = "long",
	Integer = "integer",
	Short = "short",
	String = "string",
	Date = "datetime",
	Hyperlink = "Hyperlink"
}

export interface ConnectwiseCustomField {
	caption: string;
	entryMethod: string;
	id: number;
	numberOfDecimals: 0;
	type: ConnectwiseCustomFieldType;
}

export interface ConnectwiseSystemField {
	cw_field_id: string;
	cw_field_label: string;
	cw_field_type: ConnectwiseSystemFieldType;
}

export interface ConnectwiseCredentailsTestResponse {
	existing?: any;
	integrated_pipeline_id: string;
	pipelines: Pipeline[];
	company_types: CommonKeyValuePair[];
	contact_types: CommonKeyValuePair[];
	opportunity_stages: CommonKeyValuePair[];
	opportunity_statuses: OpportunityStatus[];
	opportunity_ratings: CommonKeyValuePair[];
	business_units: CommonKeyValuePair[];
	product_types: CommonKeyValuePair[];
	product_categories: ProductCategory[];
	product_classes: string[];
	unit_of_measures: CommonKeyValuePair[];
	markets: CommonKeyValuePair[];
	locations: CommonKeyValuePair[];
	generate_fields?: boolean;
	is_item_custom_fields_non_editable?: boolean;
	tax_rates: ConnectwiseTaxImport;
	zomentum_unit_of_measures: string[];
	id_mappable_item_custom_fields: CustomField[];
	members: CommonKeyValuePair[];
	users: UserData[];
	use_stage_mapping_only?: boolean;
	client_company_custom_fields: CustomField[];
	client_user_custom_fields: CustomField[];
	item_custom_fields: CustomField[];
	opportunity_custom_fields: CustomField[];
	cw_client_company_custom_fields: ConnectwiseCustomField[];
	cw_client_user_custom_fields: ConnectwiseCustomField[];
	cw_product_custom_fields: ConnectwiseCustomField[];
	cw_product_item_custom_fields: ConnectwiseCustomField[];
	cw_opportunity_custom_fields: ConnectwiseCustomField[];
	cw_client_company_system_fields: ConnectwiseSystemField[];
	cw_client_user_system_fields: ConnectwiseSystemField[];
	cw_item_system_fields: ConnectwiseSystemField[];
	cw_opportunity_system_fields: ConnectwiseSystemField[];
}

export enum ImportTaxOption {
	Import = "import",
	DeactivateCurrentAndImport = "deactivate_current_and_import"
}

export interface CWTaxCode {
	id: number;
	name: string;
	percentage: number | null;
}

export enum TaxRegionTaxable {
	Taxable = "Taxable",
	NonTaxable = "Non taxable"
}

export interface CWTaxRegion {
	category_id: string;
	category_name: TaxRegionTaxable;
	percentage: number;
	region_id: string;
	region_name: string;
}

export interface ConnectwiseTaxImport {
	tax_region_info: CWTaxRegion[];
	tax_code_info: CWTaxCode[];
	mapping?: TaxMapping[];
}

export interface CwTaxImportAsyncHandle {
	id: string;
	operation_status: string;
}

export interface CwTaxImportOperationResult {
	id: string;
	operation_status: string;
	metadata?: {
		tax_region_info: CWTaxRegion[];
		tax_code_info: CWTaxCode[];
		mapping: TaxMapping[];
		failure_message: string | null;
	};
}
interface ProductCategory {
	id: number;
	name: string;
	sub_categories: CommonKeyValuePair[];
}

interface OpportunityStatus {
	id: number;
	name: string;
	won_flag: boolean;
	lost_flag: boolean;
	closed_flag: boolean;
}

export interface CommonKeyValuePair {
	id: ReactText;
	name: string;
}

export enum ClientCompaniesToImport {
	AllClientCompanyHierarchy = "AllClientCompanyHierarchy",
	ClientCompanyHierarchyWithAtLeastOneOpportunity = "ClientCompanyHierarchyWithAtLeastOneOpportunity",
	NoClientCompanies = "NoClientCompanies"
}

export enum SyncOption {
	ImportOnly = "ImportOnly",
	ExportOnly = "ExportOnly",
	TwoWaySync = "TwoWaySync",
	RealTimeSync = "RealTimeSync"
}

export enum EstimateOption {
	SINGLE_ESTIMATE = "Single Estimate",
	MULTIPLE_ESTIMATE = "Multiple Estimate"
}
export enum EstimateOptionsValues {
	SINGLE_ESTIMATE = "single_estimate",
	MULTIPLE_ESTIMATE = "multiple_estimate"
}

export enum ProductExportMode {
	Individual = "individual",
	Bundle = "bundle",
	ConnectwiseBundle = "connectwiseBundle"
}

export enum CWProductFieldMappingName {
	Identifier = "identifier",
	Description = "description",
	CustomerDescription = "customerDescription"
}

export class CWProductFieldMapping {
	name: CWProductFieldMappingName | null =
		CWProductFieldMappingName.Identifier;
	connectwise_product_id_mapping = "";
}

export class ConnectWiseIntegration {
	connected_user = "";
	credentials_created = "";
	credentials_updated = "";
	host = "";
	connectwise_company_id = "";
	public_api_key = "";
	private_api_key = "";
	sync_option?: SyncOption = SyncOption.ExportOnly;
	mode: IntegrationMode = IntegrationMode.CRM;
	client_companies_to_import: ClientCompaniesToImport =
		ClientCompaniesToImport.NoClientCompanies;
	company_types_to_import: string[] = new Array<string>();
	contact_types_to_import: string[] = new Array<string>();
	company_markets_to_import: string[] = new Array<string>();
	should_import_product = false;
	document_push_filter = false;
	default_values: ConnectwiseDefaultValues = new ConnectwiseDefaultValues();
	stage_mapping: StageMapping[] = new Array<StageMapping>();
	product_mapping: ProductMapping[] = new Array<ProductMapping>();
	product_field_mapping: CWProductFieldMapping | null =
		new CWProductFieldMapping();
	opportunity_owners_to_import?: string[] = new Array<string>();
	opportunity_stages_to_import: string[] = new Array<string>();
	opportunity_status_to_import: string[] = new Array<string>();
	opportunity_updated_at_filter: Date = new Date();
	generate_fields?: boolean;
	is_item_custom_fields_non_editable?: boolean;
	product_sync_details: ConnectwiseProductSync | null = null;
	product_export_mode: ProductExportMode | null = null;
	tax_mapping: TaxMapping[] = new Array<TaxMapping>();
	is_tax_imported?: boolean;
	unit_of_measure_mapping: ConnectwiseUOM[] = new Array<ConnectwiseUOM>();
	should_push_closed_opportunity = false;
	push_opportunity_on_terminal_stage = true;
	is_initial_sync_pending = false;
	block_negative_quantity?: boolean | null;
	block_zero_quantity?: boolean | null;
	item_custom_field_mapping: ConnectwiseCustomFieldMapping[] = [];
	client_user_custom_field_mapping: ConnectwiseCustomFieldMapping[] = [];
	client_company_custom_field_mapping: ConnectwiseCustomFieldMapping[] = [];
	opportunity_custom_field_mapping: ConnectwiseCustomFieldMapping[] = [];
}

export interface SyncroCredentailsTestResponse {
	existing?: any;
	pipeline: Pipeline;
	product_categories: SyncroProductCategoryView[];
	generate_fields?: boolean;
}

export interface SyncroConnectFormValues {
	api_key: string;
	sub_domain: string;
}

export interface SyncroSettingsFormValues {
	sync_option: SyncOption;
	client_created_at_filter?: Date;
	should_import_product?: boolean;
	product_category_to_import?: string[];
	should_import_client_hierarchy: boolean;
	should_split_line_items: boolean;
	push_tax_inclusive_amount_in_quotes: boolean;
}

export class SyncroSettingsRequest {
	sync_option: SyncOption = SyncOption.ExportOnly;
	client_created_at_filter?: Date;
	should_import_product?: boolean;
	product_category_to_import?: string[] | null;
	product_category_ids_to_import?: string[] | null;
	should_import_client_hierarchy = false;
	should_split_line_items = false;
	push_tax_inclusive_amount_in_quotes = true;
}

export interface ProductMapping {
	zomentum_product_type: ProductType;
	product_type?: CommonKeyValuePair;
	category?: CommonKeyValuePair;
	sub_category?: CommonKeyValuePair;
	product_class?: string;
}

export interface CWProductFieldMappingFormValues {
	readonly product_type?: string;
	readonly product_class?: string;
	readonly category?: string[];
}

export interface CWStageMappingFormValues {
	readonly opportunity_stage?: string;
	readonly opportunity_status?: string;
}

export interface ConnectwiseUOMFormValue {
	cw_unit_of_measure: string;
}

export interface ConnectwiseFieldMappingFormValues {
	product_mapping: Record<ProductType, CWProductFieldMappingFormValues>;
	stage_mapping: Record<string, CWStageMappingFormValues>;
	default_mapping: ConnectwiseFieldMappingDefaultMappingFormValue;
	tax_mapping: Record<string, string>;
	product_field_mapping: CWProductFieldMappingName;
	generate_fields: boolean;
	is_item_custom_fields_non_editable: boolean;
	unit_of_measure_mapping: ConnectwiseUOMFormValue[];
	connectwise_product_id_mapping: string;
	opportunity_sync_info: OpportunitySyncInfo;
	opportunity_custom_field_mapping?: string[];
	client_user_custom_field_mapping?: string[];
	client_company_custom_field_mapping?: string[];
	item_custom_field_mapping?: string[];
}

export interface ConnectwiseFieldMappingDefaultMappingFormValue {
	readonly pipeline_stage_id?: string;
	readonly opportunity_status?: string;
	readonly opportunity_stage?: string;
	readonly opportunity_rating?: string;
	readonly business_unit_id?: string;
	readonly product_type?: string;
	readonly product_class?: string;
	readonly category?: string[];
	readonly sub_category?: string;
	readonly location_id?: string;
	readonly tax_code?: string;
}

export interface StageMapping {
	pipeline_stage_id: string;
	stage?: CommonKeyValuePair;
	status?: CommonKeyValuePair;
}
export class SyncroFieldMappingRequest {
	product_mapping: SyncroProductMapping[] = new Array<SyncroProductMapping>();
	generate_fields = false;
}
export interface SyncroProductCategoryView {
	id: number;
	account_id: number;
	ancestry?: string;
	name?: string;
	description?: string;
	device_product_id?: string;
	names_depth_cache?: string;
}

export class StringifySyncroProduct {
	id: number;
	account_id: number;
	ancestry?: string;
	name?: string;
	description?: string;
	device_product_id?: string;
	names_depth_cache?: string;
	constructor(args: {
		id: number;
		account_id: number;
		ancestry?: string;
		name?: string;
		description?: string;
		device_product_id?: string;
		names_depth_cache?: string;
	}) {
		this.id = args.id;
		this.account_id = args.account_id;
		this.ancestry = args.ancestry;
		this.name = args.name;
		this.description = args.description;
		this.device_product_id = args.device_product_id;
		this.names_depth_cache = args.names_depth_cache;
	}
}

export interface SyncroProductMapping {
	zomentum_product_type: ProductType;
	product_category?: SyncroProductCategoryView;
}

export class AddSyncroIntegrationRequest {
	api_key?: string;
	sub_domain?: string;
	two_way_sync?: boolean;
	create_entities_as_user_id?: string;
	sync_option?: SyncOption;
	should_import_client_hierarchy?: boolean;
	should_import_product?: boolean;
	client_created_at_filter?: Date;
	product_category_to_import?: string[] | null = null;
	product_category_ids_to_import?: string[] | null = null;
	product_mapping?: SyncroProductMapping[] =
		new Array<SyncroProductMapping>();
	should_split_line_items?: boolean;
	generate_fields = false;
	push_tax_inclusive_amount_in_quotes = true;
}

export class SyncroCredentialsTestRequest {
	api_key = "";
	sub_domain = "";
	constructor(args: { api_key: string; sub_domain: string }) {
		this.api_key = args.api_key;
		this.sub_domain = args.sub_domain;
	}
}

export class SyncroIntegration {
	connected_user = "";
	credentials_created: Date = new Date();
	credentials_updated: Date = new Date();
	should_import_client_hierarchy = false;
	should_import_product = false;
	client_created_at_filter?: Date = new Date();
	product_category_to_import?: string[] = new Array<string>();
	product_category_ids_to_import?: string[] = new Array<string>();
	sync_option: SyncOption = SyncOption.ExportOnly;
	product_mapping?: SyncroProductMapping[] =
		new Array<SyncroProductMapping>();
	should_split_line_items = false;
	generate_fields = false;
	is_initial_sync_pending = false;
	block_negative_quantity?: boolean | null;
	block_zero_quantity?: boolean | null;
	push_tax_inclusive_amount_in_quotes = true;
}

export class RepairShoprIntegration {
	connected_user = "";
	credentials_created: Date = new Date();
	credentials_updated: Date = new Date();
	should_import_client_hierarchy = false;
	should_import_product = false;
	client_created_at_filter?: Date = new Date();
	product_category_to_import?: RepairShoprProductCategoryView[] =
		new Array<RepairShoprProductCategoryView>();
	sync_option: SyncOption = SyncOption.ExportOnly;
	product_mapping?: RepairShoprProductMapping[] =
		new Array<RepairShoprProductMapping>();
	should_split_line_items = false;
	generate_fields = false;
	is_initial_sync_pending = false;
	block_negative_quantity?: boolean | null;
	block_zero_quantity?: boolean | null;
}

export enum EmailIntegrationProvider {
	Gmail = "gmail",
	EAS = "eas",
	EWS = "ews",
	Microsoft = "microsoft",
	Google = "google"
}

export type EmailAlias = Omit<EmailThreadParticipant, "email"> & {
	email: string;
};

export enum AccountVisibilityStatus {
	Public = "public",
	Private = "private"
}

export class EmailIntegration {
	account_id = "";
	email = "";
	provider: EmailIntegrationProvider = EmailIntegrationProvider.Gmail;
	user_id = "";
	email_aliases: EmailAlias[] | null = new Array<EmailAlias>();
	calendar_connected_settings: CalendarConnectedSettings | null = null;
	connected_modules: string[] = new Array<string>();
	account_visibility_state: AccountVisibilityStatus =
		AccountVisibilityStatus.Public;
}

export interface UnsubscribeEmailsInterface {
	should_disable_daily?: boolean;
	should_disable_weekly?: boolean;
}

export enum EmailDigestType {
	DAILY = "Daily",
	WEEKLY = "Weekly"
}

export interface CalendarConnectedSettings {
	calendar_name: string;
	calendar_id: string;
	two_way_sync: boolean;
	one_way_sync: boolean;
	should_sync_tasks_from_zomentum_to_calendar: boolean;
	should_create_contacts_from_attendees: boolean;
	connection_status: string;
	ignored_domains: string[];
}

export class ConnectCalendarPostRequest {
	calendar_id = "";
	two_way_sync = false;
	one_way_sync = false;
	should_sync_tasks_from_zomentum_to_calendar = false;
	should_create_contacts_from_attendees = false;
	ignored_domains: string[] = new Array<string>();
}

export class EndUserLicenseAgreement {
	acceptance_time = 0;
	accepted = false;
	accepted_by_user = "";
	accepted_version = "";
}

export class UserCredentials {
	password = "";
	userName = "";
}

export class KaseyaIntegration {
	connected_user = "";
	last_synced_at = "";
	two_way_sync = true;
	mode?: IntegrationMode;
	bundle_push_mode?: KaseyaPushBundleLevelOption;
	push_quotations?: boolean;
	product_sync_detail: KaseyaProductSyncDetails | null = null;
	pull_updates = false;
	default_values?: KaseyaDefaultValues;
	opportunity_status_mappings?: KaseyaOpportunityStatusMapping[];
	multiple_pipeline_pull_mapping?: KaseyaOpportunityPipelineMapping[];
	multiple_pipeline_push_mapping?: KaseyaOpportunityPipelineMapping[];
	document_status_to_kaseya_quote_status_mapping?: KaseyaDocumentQuoteMapping[];
	product_export_mappings?: KaseyaProductMapping;
	product_import_mappings?: KaseyaProductMapping;
	is_initial_sync_pending = false;
	block_negative_quantity?: boolean | null;
	block_zero_quantity?: boolean | null;
}

export class AutotaskIntegration {
	connected_user = "";
	last_synced_at = 0;
	two_way_sync = false;
	is_initial_sync_pending = false;
	block_negative_quantity?: boolean | null;
	block_zero_quantity?: boolean | null;
}

export class ZapierIntegration {
	connected_users: string[] = new Array<string>();
	last_synced_at = 0;
}

export enum IngramCredentialType {
	XML = "xml",
	API = "api"
}

export class IngramIntegration {
	connected_user = "";
	country_code?: string;
	cred_type: IngramCredentialType = IngramCredentialType.XML;
}

export class TechDataIntegration {
	connected_user = "";
	country_code?: string;
}

export class DAndHIntegration {
	connected_user = "";
	country_code?: string;
}

export class SynnexIntegration {
	connected_user = "";
	country_code?: string;
}

export class ConnectBoosterIntegration {
	url?: string;
	api_key?: string;
	currency?: string;
	connected_user?: string;
}
export class StripeIntegration {
	account_id = "";
}

export enum PaymentsState {
	Pending = "pending",
	Valid = "valid",
	Rejected = "rejected",
	Invalid = "invalid"
}

export class ZMPaymentsIntegraton {
	receive_payments: PaymentsState = PaymentsState.Invalid;
	payouts_type: PaymentsState = PaymentsState.Invalid;
	card_status = false;
	direct_debit_status = false;
}

export enum DistributorRegions {
	USA = "us",
	Canada = "ca",
	UK = "uk",
	Australia = "au",
	NZ = "nz",
	BE = "be",
	AT = "at",
	CH = "ch",
	DE = "de",
	SE = "se",
	ES = "es",
	FR = "fr",
	IT = "it",
	PT = "pt",
	HU = "hu",
	Netherlands = "nl"
}

export enum IngramRegions {
	us = "USA",
	ca = "Canada",
	uk = "UK",
	au = "Australia",
	nz = "New zealand",
	at = "Austria",
	ch = "Switzerland",
	de = "Germany",
	se = "Sweden",
	es = "Spain",
	fr = "France",
	it = "Italy",
	pt = "Portugal",
	hu = "Hungary",
	nl = "Netherlands"
}

export const IngramApiRegions = [
	DistributorRegions.USA,
	DistributorRegions.Canada,
	DistributorRegions.UK,
	DistributorRegions.AT,
	DistributorRegions.CH,
	DistributorRegions.DE,
	DistributorRegions.SE,
	DistributorRegions.ES,
	DistributorRegions.FR,
	DistributorRegions.IT,
	DistributorRegions.PT,
	DistributorRegions.HU,
	DistributorRegions.BE,
	DistributorRegions.Australia,
	DistributorRegions.NZ,
	DistributorRegions.Netherlands
];

export const IngramXmlRegions = [
	DistributorRegions.USA,
	DistributorRegions.Canada,
	DistributorRegions.UK,
	DistributorRegions.NZ,
	DistributorRegions.Australia,
	DistributorRegions.Netherlands
];

export const TechDataCustomerNoRegions = [
	DistributorRegions.UK,
	DistributorRegions.BE
];

export interface DickerDataCreateOrUpdateRequest {
	readonly access_token?: string;
	readonly account_code?: string;
	readonly region?: DistributorRegions;
	readonly url?: string;
	readonly distributor_connection_type?: string;
}

export interface AddDickerDataIntegrationFormValues {
	readonly access_token?: string;
	readonly account_code?: string;
	readonly region?: DistributorRegions;
	readonly url?: string;
	readonly distributor_connection_type?: string;
}

export interface DickerDataIntegration {
	readonly connected_user: string;
	readonly country_code?: string;
	readonly connection_type?: string;
	readonly connection_details?: DickerDataConnectionDetails;
}

export interface DickerDataConnectionDetails {
	readonly email: string | null;
	readonly url: string | null;
	readonly updated_at?: string | null;
}

export const prefillContent = "****************************";

export class InfusionSoftIntegration {
	connected_user = "";
	two_way_sync = false;
	is_initial_sync_pending = false;
}

export interface InfusionSoftSettingsFormValues {
	sync_option: SyncOption;
	should_sync_opp_related_data_only: boolean;
	should_push_pdf_under_infusion: boolean;
	sync_opportunity_non_associated: boolean;
}

export class InfusionSoftSettingsRequest {
	sync_option: SyncOption = SyncOption.ExportOnly;
	should_sync_opp_related_data_only = false;
}

export interface InfusionSoftField {
	infusion_soft_field_id: string;
	infusion_soft_field_label: string;
}

export interface ZomentumOptionValue {
	label: string;
	value: string;
}

export interface InfusionSoftStageMappingValuePair {
	infusion_soft_value: ZomentumOptionValue;
	zomentum_value: ZomentumOptionValue;
}

export interface InfusionSoftStageMapping {
	can_edit: boolean;
	default_infusion_soft_value: string | null;
	infusion_soft_label: string;
	pipeline_id: string;
	zomentum_label: string;
	value_mapping: InfusionSoftStageMappingValuePair[];
}

export interface InfusionSoftStageMappingRequestMapping {
	value_mapping: InfusionSoftStageMappingValuePair[];
	pipeline_id: string;
	default_infusion_soft_mapping: string | null;
}

export enum InfusionSoftStageMappingField {
	opportunity_stage = "opportunity_stage"
}

export interface InfusionSoftStageMappingRequest {
	infusion_soft_label: string;
	zomentum_label: string;
	can_edit: boolean;
	field: InfusionSoftStageMappingField;
	default_infusion_soft_value: string | null;
	mapping: InfusionSoftStageMappingRequestMapping;
}

export interface InfusionSoftMetadataResponse {
	all_infusion_soft_client_company_fields: InfusionSoftField[];
	all_infusion_soft_client_user_fields: InfusionSoftField[];
	all_infusion_soft_opportunity_fields: InfusionSoftField[];
	all_infusion_soft_opportunity_stages: InfusionSoftField[];
	client_company_mapping: InfusionSoftEntityMapping;
	client_user_mapping: InfusionSoftEntityMapping;
	opportunity_mapping: InfusionSoftEntityMapping;
	opportunity_stage_mapping: InfusionSoftStageMapping;
	sync_property: SyncOption;
	should_sync_opp_related_data_only: boolean | null;
}

export interface InfusionSoftMetadataRequest {
	sync_option: SyncOption;
	create_entities_as_user_id: string;
	opportunity_mapping: InfusionSoftEntityMapping;
	client_company_mapping: InfusionSoftEntityMapping;
	client_user_mapping: InfusionSoftEntityMapping;
	opportunity_stage_mapping: InfusionSoftStageMappingRequest;
	should_sync_opp_related_data_only?: boolean;
}

export interface InfusionSoftMapping {
	infusion_soft_field: string;
	zomentum_field: string;
	sync_option: SyncOption;
	can_edit: boolean;
}

export interface InfusionSoftMappingFieldData {
	can_edit: boolean;
	field: string;
	infusion_soft_label: string | null;
	mapping: InfusionSoftMapping | null;
	zomentum_label: string;
}

export interface InfusionSoftEntityMapping {
	entity?: string;
	fields: InfusionSoftMappingFieldData[];
}

export interface InfusionSoftStageMappingFormValue {
	id: string;
}

export interface InfusionSoftFieldMappingFormValues {
	opportunity_mapping: InfusionSoftEntityMapping;
	client_user_mapping: InfusionSoftEntityMapping;
	client_company_mapping: InfusionSoftEntityMapping;
	opportunity_stages: InfusionSoftStageMappingFormValue[];
}

export interface InfusionSoftFieldMapping {
	opportunity_mapping: InfusionSoftEntityMapping;
	client_user_mapping: InfusionSoftEntityMapping;
	client_company_mapping: InfusionSoftEntityMapping;
	opportunity_stage_mapping: InfusionSoftStageMapping;
}

export enum CustomDistributorConnectionMethods {
	FTP = "ftp",
	SFTP = "sftp",
	MANUAL = "manual",
	API = "api",
	SECURE_API = "via_secure_url"
}

export enum CustomDistributorConnectionType {
	PUSH = "push",
	PULL = "pull"
}

export enum ConnectionTypeServerKey {
	VIA_URL = "via_url",
	VIA_FTP = "via_ftp_pull",
	VIA_SFTP = "via_sftp_pull",
	VIA_FTP_PUSH = "via_ftp_push",
	VIA_API_PUSH = "via_api_push",
	VIA_MANUAL_PUSH = "via_manual_push",
	VIA_SECURE_URL = "via_secure_url"
}

export const getConnectionTypeServerKey = (
	connectionType: CustomDistributorConnectionType,
	connectionMethod: CustomDistributorConnectionMethods
): ConnectionTypeServerKey => {
	if (connectionType === CustomDistributorConnectionType.PULL) {
		switch (connectionMethod) {
			case CustomDistributorConnectionMethods.API:
				return ConnectionTypeServerKey.VIA_URL;
			case CustomDistributorConnectionMethods.FTP:
				return ConnectionTypeServerKey.VIA_FTP;
			case CustomDistributorConnectionMethods.SFTP:
				return ConnectionTypeServerKey.VIA_SFTP;
			case CustomDistributorConnectionMethods.SECURE_API:
				return ConnectionTypeServerKey.VIA_SECURE_URL;
			default:
				return ConnectionTypeServerKey.VIA_URL;
		}
	} else {
		switch (connectionMethod) {
			case CustomDistributorConnectionMethods.API:
				return ConnectionTypeServerKey.VIA_API_PUSH;
			case CustomDistributorConnectionMethods.FTP:
			case CustomDistributorConnectionMethods.SFTP:
				return ConnectionTypeServerKey.VIA_FTP_PUSH;
			case CustomDistributorConnectionMethods.MANUAL:
				return ConnectionTypeServerKey.VIA_MANUAL_PUSH;
			default:
				return ConnectionTypeServerKey.VIA_API_PUSH;
		}
	}
};

export class CustomDistributorFTPConnectionRequest {
	user_name? = "";
	password? = "";
	server? = "";
	remote_folder?: string;
	file_name?: string;
	port?: number;
}

export type CustomDistributorFileDetailsRequest = Partial<{
	url: string;
	user_name?: string;
	password?: string;
}> &
	Partial<CustomDistributorFTPConnectionRequest>;

export interface CustomDistributorFileDetailsResponse {
	file_name: string;
	file_type: string;
}

export interface CustomDistributorBasicDetail {
	vendor: string;
	integration_name: string;
}

export class MetaDataRequest {
	connection_type: ConnectionTypeServerKey = ConnectionTypeServerKey.VIA_URL;
	url?: string;
	file?: File;
	id = "";
	file_type?: string;
	file_separator?: string;
	custom_distributor_name: string = "";
}

export interface FetchMetaDataRequest {
	file_type: string | null;
	file_separator: string | null;
	connection_details: {
		connection_type: ConnectionTypeServerKey;
		data: CustomDistributorFileDetailsRequest;
	};
	custom_distributor_name: string;
}

export class CustomDistributorSecureApiConnectionRequest {
	user_name? = "";
	password? = "";
}

export class PricingField {
	field = "";
	display_name?: string;
	accepted_values?: string[];
}

export class HeadersField {
	field = "";
	display_name = "";
	data_type = "";
	zomentum_field = "";
}

export interface MetaDataResponse {
	file_headers: string[];
	file_name: string;
	pricing_fields: PricingField[];
	headers: HeadersField[];
	sample_data: Record<string, string>[];
	row_count: number;
}

export class FieldMappingRequest {
	distributor_field = "";
	zomentum_field = "";
}

export class FieldMappingResponse {
	distributor_field: string | null = null;
	zomentum_field: PricingField = new PricingField();
}

export interface ConnectionDetails {
	connection_type: ConnectionTypeServerKey;
	data?: any;
}

export class CustomDistributorUpdateRequest {
	id = "";
	mappings: FieldMappingRequest[] = [];
	file_name?: string;
	integration_name = "";
	vendor = "";
	connection_details: ConnectionDetails = {
		connection_type: ConnectionTypeServerKey.VIA_URL,
		data: {}
	};
	file_type?: string | null;
	file_separator?: string | null;
	decimal_digits_separator?: string | null;
}

export interface CustomDistributorAuthDetailsResponse {
	api_endpoint: string;
	ftp_endpoint: string;
	username: string;
	password: string;
}

export class ConnectionDetailsResponse {
	connection_type: ConnectionTypeServerKey = ConnectionTypeServerKey.VIA_URL;
	details?: any;
}
export class CustomDistributorIntegration {
	id = "";
	user_id = "";
	integration_name = "";
	vendor = "";
	connection_details: ConnectionDetailsResponse =
		new ConnectionDetailsResponse();
	mappings: FieldMappingResponse[] = [];
	file_name = "";
	file_type?: string | null;
	file_separator?: string | null;
	decimal_digits_separator?: string | null;
	is_system_integration?: boolean;
	last_updated_at?: string;
}

export interface CustomDistributorEditFormValues {
	vendor: string;
	distributor_name: string;
}

export enum CustomDistributorPageType {
	EDIT = "EDIT",
	ADD = "ADD"
}

export class Integrations {
	connectwise: ConnectWiseIntegration = new ConnectWiseIntegration();
	email: EmailIntegration[] = new Array<EmailIntegration>();
	autotask: AutotaskIntegration = new AutotaskIntegration();
	kaseya: KaseyaIntegration | null = new KaseyaIntegration();
	zapier: ZapierIntegration = new ZapierIntegration();
	ingram: IngramIntegration = new IngramIntegration();
	tech_data: TechDataIntegration = new TechDataIntegration();
	d_and_h: DAndHIntegration = new DAndHIntegration();
	synnex: SynnexIntegration = new SynnexIntegration();
	syncro: SyncroIntegration = new SyncroIntegration();
	repair_shopr: RepairShoprIntegration = new RepairShoprIntegration();
	connect_booster: ConnectBoosterIntegration =
		new ConnectBoosterIntegration();
	dicker_data?: DickerDataIntegration;
	infusion_soft: InfusionSoftIntegration = new InfusionSoftIntegration();
	hubspot: HubspotIntegration | null = new HubspotIntegration();
	westcoast: WestcoastIntegration | null = null;
	stripe: StripeIntegration | null = null;
	quickbooks: QuickbooksIntegration | null = null;
	halo: HaloPSAIntegration | null = null;
	custom_distributor: CustomDistributorIntegration[] | null = null;
	payments: ZMPaymentsIntegraton | null = null;
	integrations_page_details = new IntegrationPageDetails();
	amazon_business: AmazonIntegration | null = null;
	superops_opt?: { [key: string]: unknown } | null = null;
}
export class IntegrationPageDetails {
	favourite_integrations: FavouriteIntegrationDetail[] = [];

	integrations_page_visited = false;
}

export type favouriteIntegrationType = "PSA" | "Distributor";
export interface FavouriteIntegrationDetail {
	name: string;
	type: favouriteIntegrationType;
}

// Every new integration (Distributors) goes below too for etilize to map
export enum Distributors {
	Ingram = "ingram",
	TechData = "tech_data",
	DAndH = "d_and_h",
	Synnex = "synnex",
	DickerData = "dicker_data",
	Westcoast = "westcoast",
	CustomDistributor = "custom_distributor"
}

export enum Locale {
	US = "en-US",
	UK = "en-UK",
	AU = "en-AU"
}

export enum EtilizeLocale {
	US = "en_us",
	UK = "en_uk",
	AU = "en_au",
	NL = "en_nl",
	BE = "en_be",
	DE = "en_de",
	FR = "en_fr"
}

export enum SimilarEtilzieRegions {
	ca = "us",
	nz = "au"
}

interface ProductContentSource {
	name: string;
	value: EtilizeLocale;
}
export const etilizeProductContentSources: Array<ProductContentSource> = [
	{ name: "North America (USA and Canada)", value: EtilizeLocale.US },
	{ name: "United Kingdom (UK)", value: EtilizeLocale.UK },
	{ name: "Australia (AU)", value: EtilizeLocale.AU },
	{ name: "Netherlands (NL)", value: EtilizeLocale.NL },
	{ name: "Belgium (BE)", value: EtilizeLocale.BE },
	{ name: "Germany (DE)", value: EtilizeLocale.DE }
];

export const icecatLocaleArray: Array<ProductContentSource> = [
	{ name: "North America (USA and Canada)", value: EtilizeLocale.US },
	{
		name: "United Kingdom of Great Britain and Northern Ireland (UK)",
		value: EtilizeLocale.UK
	},
	{ name: "Australia (AU & NZ)", value: EtilizeLocale.AU },
	{ name: "Netherlands (NL)", value: EtilizeLocale.NL },
	{ name: "Belgium (BE)", value: EtilizeLocale.BE },
	{ name: "Germany (DE)", value: EtilizeLocale.DE },
	{ name: "France (FR)", value: EtilizeLocale.FR }
];

export class IntegrationsResponse {
	base = "";
	integrations: Integrations = new Integrations();
}

export class AddEmailIntegrationRequest {
	state = "";
	code = "";
}

export class DeleteConnectWiseIntegrationRequest {
	connectwise: ConnectWiseIntegration = new ConnectWiseIntegration();
}

export interface AutotaskConnectFormValues {
	user_name: string | null;
	password: string | null;
}

export interface AutotaskTaxImportRequest {
	user_name: string;
	password: string;
	disable_existing_taxes: boolean;
}

export interface ConnectwiseConnectFormValues {
	region: ConnectwiseRegions;
	host: string;
	connectwise_company_id: string;
	public_api_key: string;
	private_api_key: string;
}

export interface AutotaskSettingsFormValues {
	readonly sync_property?: AutotaskSyncProperty;
	readonly sync_options?: {
		readonly is_enabled: boolean;
		readonly advanced_options: {
			readonly is_enabled: boolean;
			readonly values: boolean | string | string[] | moment.Moment;
		}[];
	}[];
	readonly mapping: AutotaskMapping[];
	readonly mode: IntegrationMode;
	readonly default_options?: AutotaskDefaultOption[] | null;
}

export interface ConnectwiseSettingsFormValues {
	sync_option?: SyncOption;
	client_companies_to_import: ClientCompaniesToImport;
	company_types_to_import: string[];
	contact_types_to_import: string[];
	company_markets_to_import: string[];
	should_import_product: boolean;
	document_push_filter: boolean;
	opportunity_location?: number;
	business_unit_id?: number;
	opportunity_rating?: string;
	location_id?: number;
	mode: IntegrationMode;
	zomentum_owner_id: string;
	connectwise_member: string;
	opportunity_stages_to_import?: string[];
	opportunity_status_to_import?: string[];
	opportunity_updated_at_filter?: Date;
	overwrite_zomentum_products?: boolean;
	overwrite_connectwise_products?: boolean;
	product_export_mode?: ProductExportMode;
	product_export_mode_checkbox?: boolean;
	should_push_closed_opportunity: boolean;
}

export enum IntgerationPullSelectOption {
	PullAll = "PullAll",
	PullSelected = "PullSelected"
}

export interface IntegrationPullTypesState {
	com_pull_type: IntgerationPullSelectOption;
	opp_pull_type: IntgerationPullSelectOption;
}

export interface ConnectwiseCompanyPullFiltersFormValues {
	company_type_checkbox: boolean;
	company_types_to_import: string[];
	company_market_checkbox: boolean;
	company_markets_to_import: string[];
	company_with_opportunity_attached: boolean;
}

export interface ConnectwiseOpportunityPullFiltersFormValues {
	opportunity_owner_checkbox: boolean;
	opportunity_owners_to_import: string[];
	opportunity_stage_checkbox: boolean;
	opportunity_stages_to_import: string[];
	opportunity_status_checkbox: boolean;
	opportunity_status_to_import: string[];
	opportunity_updated_at_filter_checkbox: boolean;
	opportunity_updated_at_filter?: Date;
}

export interface ConnectwiseSettingsFormValuesV2 {
	create_client_on_opportunity_push: boolean;
	push_opportunity_on_terminal_stage: boolean;
	should_push_closed_opportunity: boolean;
	document_push_filter: boolean;
	product_export_mode?: ProductExportMode;
	location_id?: number;
	business_unit_id?: number;
	opportunity_rating?: string;
	connectwise_member: string;
	zomentum_owner_id: string;
	pull_company_selects: IntgerationPullSelectOption;
	pull_opportunity_selects: IntgerationPullSelectOption;
	company_pull_filters: ConnectwiseCompanyPullFiltersFormValues;
	opportunity_pull_filters: ConnectwiseOpportunityPullFiltersFormValues;
}

export class ConnectwiseDefaultValues {
	pipeline_stage_id?: string;
	opportunity_status?: CommonKeyValuePair;
	opportunity_stage?: CommonKeyValuePair;
	opportunity_rating?: CommonKeyValuePair;
	business_unit_id?: number;
	product_type?: CommonKeyValuePair;
	product_class?: string;
	// opportunity_location?: CommonKeyValuePair; // change this to correct field
	category?: CommonKeyValuePair;
	sub_category?: CommonKeyValuePair;
	location_id?: number;
	tax_code?: CommonKeyValuePair;
	unit_of_measure?: CommonKeyValuePair;
	connectwise_member?: CommonKeyValuePair | null;
	zomentum_owner_id?: string | null;
	pull_as_won?: boolean;
	pull_as_lost?: boolean;
	use_stage_mapping_only?: boolean;
}

export interface ConnectwiseProductSync {
	overwrite_zomentum_with_cw: boolean;
	overwrite_cw_with_zomentum: boolean;
}

export class ConnectwiseCustomFieldMapping {
	cw_custom_field: string | null = null;
	cw_system_field: string | null = null;
	zm_custom_field = "";

	constructor(
		zmId: string,
		cwCustomId: string | null,
		cwSystemId: string | null
	) {
		this.cw_custom_field = cwCustomId;
		this.cw_system_field = cwSystemId;
		this.zm_custom_field = zmId;
	}
}

export class ConnectwiseSettingsRequest {
	sync_option?: SyncOption | null;
	client_companies_to_import: ClientCompaniesToImport =
		ClientCompaniesToImport.NoClientCompanies;
	company_types_to_import: string[] = new Array<string>();
	contact_types_to_import: string[] = new Array<string>();
	company_markets_to_import: string[] = new Array<string>();
	should_import_product = false;
	document_push_filter = false;
	mode: IntegrationMode = IntegrationMode.CPQ;
	default_values?: ConnectwiseDefaultValues;
	opportunity_stages_to_import: string[] = new Array<string>();
	opportunity_status_to_import: string[] = new Array<string>();
	opportunity_updated_at_filter?: Date | null = null;
	product_sync_details: ConnectwiseProductSync | null = null;
	product_export_mode?: ProductExportMode | null = null;
	should_push_closed_opportunity = false;
	opportunity_owners_to_import: string[] = [];
	create_client_on_opportunity_push = false;
	push_opportunity_on_terminal_stage = true;
	item_custom_field_mapping: ConnectwiseCustomFieldMapping[] = [];
	client_user_custom_field_mapping: ConnectwiseCustomFieldMapping[] = [];
	client_company_custom_field_mapping: ConnectwiseCustomFieldMapping[] = [];
	opportunity_custom_field_mapping: ConnectwiseCustomFieldMapping[] = [];
}

export interface ProductMapping {
	zomentum_product_type: ProductType;
	product_type?: CommonKeyValuePair;
	category?: CommonKeyValuePair;
	sub_category?: CommonKeyValuePair;
	product_class?: string;
}

export interface StageMapping {
	pipeline_stage_id: string;
	stage?: CommonKeyValuePair;
	status?: CommonKeyValuePair;
}

export interface TaxMapping {
	tax_region_id: string;
	tax_region_name: string;
	tax_category_id: string;
	tax_category_name: string;
	cw_tax_code: CommonKeyValuePair;
	percentage?: number;
}

export interface ConnectwiseUOM {
	zomentum_unit_of_measure: string;
	cw_unit_of_measure: CommonKeyValuePair;
}

export class OpportunitySyncInfo {
	pull_as_won = false;
	pull_as_lost = false;
}
export class ConnectwiseFieldMappingRequest {
	default_values: Partial<ConnectwiseDefaultValues> | null = null;
	stage_mapping: StageMapping[] = new Array<StageMapping>();
	product_mapping: ProductMapping[] = new Array<ProductMapping>();
	tax_mapping: TaxMapping[] = new Array<TaxMapping>();
	generate_fields = false;
	is_item_custom_fields_non_editable = false;
	is_tax_imported?: boolean;
	product_field_mapping: CWProductFieldMapping | null =
		new CWProductFieldMapping();
	integrated_pipeline_id: string | null = "";
	unit_of_measure_mapping: ConnectwiseUOM[] = new Array<ConnectwiseUOM>();
	use_stage_mapping_only = false;
	opportunity_sync_info: OpportunitySyncInfo = new OpportunitySyncInfo();
	should_push_closed_opportunity = false;
	item_custom_field_mapping: ConnectwiseCustomFieldMapping[] = [];
	client_user_custom_field_mapping: ConnectwiseCustomFieldMapping[] = [];
	client_company_custom_field_mapping: ConnectwiseCustomFieldMapping[] = [];
	opportunity_custom_field_mapping: ConnectwiseCustomFieldMapping[] = [];
}

export class StringifiedKeyValuePair {
	id: number | string = 0;
	name = "";
	constructor(id: number | string, name: string) {
		this.id = id;
		this.name = name;
	}
}

export enum ConnectwiseRegions {
	NorthAmerica = "north_america",
	Europe = "europe",
	Australia = "australia",
	SelfHosted = "self_hosted"
}

export const ConnectwiseHost = {
	[ConnectwiseRegions.NorthAmerica]:
		"https://api-na.myconnectwise.net/v2020_2/apis/3.0/",
	[ConnectwiseRegions.Europe]:
		"https://api-eu.myconnectwise.net/v2020_2/apis/3.0/",
	[ConnectwiseRegions.Australia]:
		"https://api-au.myconnectwise.net/v2020_2/apis/3.0/"
};

export interface ConnectwiseConnectStepRequest
	extends ConnectwiseCredentialsTestRequest {
	readonly region: ConnectwiseRegions;
}

export class ConnectwiseCredentialsTestRequest {
	host: string | null = "";
	connectwise_company_id: string | null = "";
	public_api_key: string | null = "";
	private_api_key: string | null = "";

	constructor(args: {
		host: string | null;
		connectwise_company_id: string | null;
		public_api_key: string | null;
		private_api_key: string | null;
	}) {
		this.host = args.host;
		this.connectwise_company_id = args.connectwise_company_id;
		this.public_api_key = args.public_api_key;
		this.private_api_key = args.private_api_key;
	}
}
export class ImportTaxRequest {
	host?: string;
	connectwise_company_id?: string;
	public_api_key?: string;
	private_api_key?: string;
	deactivate_existing = false;
}

export enum ConnectwiseEntities {
	OPPORTUNITY = "opportunity",
	COMPANY = "company"
}

export class AddConnectWiseIntegrationRequest {
	host?: string = "";
	connectwise_company_id?: string = "";
	public_api_key?: string = "";
	private_api_key?: string = "";
	mode: IntegrationMode = IntegrationMode.CRM;
	sync_option?: SyncOption;
	client_companies_to_import?: ClientCompaniesToImport;
	company_types_to_import?: string[] = new Array<string>();
	contact_types_to_import?: string[] = new Array<string>();
	company_markets_to_import?: string[] = new Array<string>();
	opportunity_owners_to_import?: string[] = new Array<string>();
	opportunity_stages_to_import?: string[] = new Array<string>();
	opportunity_status_to_import?: string[] = new Array<string>();
	opportunity_updated_at_filter?: Date | null = null;
	should_import_product?: boolean;
	default_values?: ConnectwiseDefaultValues = new ConnectwiseDefaultValues();
	stage_mapping?: StageMapping[] = new Array<StageMapping>();
	product_mapping?: ProductMapping[] = new Array<ProductMapping>();
	generate_fields?: boolean = false;
	document_push_filter = false;
	is_item_custom_fields_non_editable = false;
	product_sync_details: ConnectwiseProductSync | null = null;
	product_export_mode: ProductExportMode | null = null;
	tax_mapping: TaxMapping[] = new Array<TaxMapping>();
	is_tax_imported?: boolean;
	product_field_mapping: CWProductFieldMapping | null =
		new CWProductFieldMapping();
	integrated_pipeline_id: string | null = "";
	unit_of_measure_mapping: ConnectwiseUOM[] = new Array<ConnectwiseUOM>();
	use_stage_mapping_only = false;
	opportunity_sync_info: OpportunitySyncInfo = new OpportunitySyncInfo();
	should_push_closed_opportunity = false;
	entity_type?: ConnectwiseEntities;
	item_custom_field_mapping: ConnectwiseCustomFieldMapping[] = [];
	client_user_custom_field_mapping: ConnectwiseCustomFieldMapping[] = [];
	client_company_custom_field_mapping: ConnectwiseCustomFieldMapping[] = [];
	opportunity_custom_field_mapping: ConnectwiseCustomFieldMapping[] = [];
	import_client_company_with_at_least_one_opportunity: boolean = false;
}

export interface ConnenctwiseIntegrationCountResponse {
	is_approximate_value: boolean;
	query_count: number;
}

export interface AddKaseyaIntegrationFormValues {
	username: string | null;
	password: string | null;
	company: string | null;
	server_endpoint: string | null;
	two_way_sync: boolean;
}

export class DeleteZapierIntegrationRequest {
	zapier: ZapierIntegration = new ZapierIntegration();
}

export interface AddIngramIntegrationFormValues {
	login_id: string | null;
	password: string | null;
	country_code: string | null;
	client_id: string | null;
	client_secret: string | null;
	customer_number: string | null;
	cred_type: IngramCredentialType;
}

export class AddIngramIntegrationRequest {
	login_id?: string | null;
	password?: string | null;
	country_code?: string | null;
	client_id?: string | null;
	client_secret?: string | null;
	customer_number?: string | null;
	cred_type: IngramCredentialType = IngramCredentialType.XML;
}

export interface AddCBIntegrationFormValues {
	url: string | null;
	api_key: string | null;
	collectjs_token: string | null;
	currency: string | null;
}

export class AddCBIntegrationRequest {
	url?: string | null;
	api_key?: string | null;
	collectjs_token?: string | null;
	currency?: string | null;
}

export interface AddTechDataIntegrationFormValues {
	login_id: string | null;
	password: string | null;
	region: string | null;
}

export class AddTechDataIntegrationRequest {
	login_id?: string | null;
	password?: string | null;
	region?: string | null;
}

export interface AddDAndHIntegrationFormValues {
	user_id: string | null;
	password: string | null;
	region: string | null;
}

export class AddDAndHIntegrationRequest {
	user_id?: string | null;
	password?: string | null;
	region?: string | null;
}

export interface AddSynnexIntegrationFormValues {
	customer_no: string | null;
	username: string | null;
	password: string | null;
	region: string | null;
	client_user_fax: string | null;
	client_user_phone: string | null;
}

export class AddSynnexIntegrationRequest {
	customer_no?: string | null;
	username?: string | null;
	password?: string | null;
	region?: string | null;
	client_user_fax?: string | null;
	client_user_phone?: string | null;
}

export interface AddWestcoastIntegrationFormValues {
	login_id: string;
	password: string;
	company_code: string;
	region: DistributorRegions;
}

export class AddWestcoastIntegrationRequest {
	login_id = "";
	password = "";
	company_code = "";
	region: DistributorRegions = DistributorRegions.UK;
}

export interface WestcoastIntegration {
	connected_user: string;
	country_code: DistributorRegions;
}

export enum CustomFieldType {
	TextField = "text_field",
	NumberField = "number_field",
	Dropdown = "dropdown_field",
	DecimalField = "decimal_field",
	SystemField = "system_field",
	DateField = "date_field",
	DropdownFieldWithKeyValue = "dropdown_field_with_key_value",
	CurrencyField = "currency_field",
	AmountField = "amount_field",
	FormulaField = "formula_field",
	MultiSelect = "multi_select_field",
	UrlField = "url_field"
}

export const ZmToCwCustomFieldTypeMap: Record<
	CustomFieldType,
	ConnectwiseCustomFieldType[]
> = {
	//empty array will show all the cw custom fields to mapped zomentum field
	[CustomFieldType.DateField]: [ConnectwiseCustomFieldType.Date],
	[CustomFieldType.TextField]: [
		ConnectwiseCustomFieldType.Button,
		ConnectwiseCustomFieldType.Checkbox,
		ConnectwiseCustomFieldType.Hyperlink,
		ConnectwiseCustomFieldType.Text,
		ConnectwiseCustomFieldType.TextArea
	],
	[CustomFieldType.UrlField]: [ConnectwiseCustomFieldType.Hyperlink],
	[CustomFieldType.NumberField]: [
		ConnectwiseCustomFieldType.Number,
		ConnectwiseCustomFieldType.Percent
	],
	[CustomFieldType.DecimalField]: [
		ConnectwiseCustomFieldType.Number,
		ConnectwiseCustomFieldType.Percent
	],
	[CustomFieldType.AmountField]: [
		ConnectwiseCustomFieldType.Number,
		ConnectwiseCustomFieldType.Percent
	],
	[CustomFieldType.Dropdown]: [],
	[CustomFieldType.SystemField]: [],
	[CustomFieldType.DropdownFieldWithKeyValue]: [],
	[CustomFieldType.CurrencyField]: [],
	[CustomFieldType.FormulaField]: [],
	[CustomFieldType.MultiSelect]: []
};

export const ZmToCwSystemFieldTypeMap: Record<
	CustomFieldType,
	ConnectwiseSystemFieldType[]
> = {
	[CustomFieldType.DateField]: [],
	[CustomFieldType.TextField]: [
		ConnectwiseSystemFieldType.Text,
		ConnectwiseSystemFieldType.Dropdown
	],
	[CustomFieldType.UrlField]: [],
	[CustomFieldType.NumberField]: [ConnectwiseSystemFieldType.Number],
	[CustomFieldType.DecimalField]: [ConnectwiseSystemFieldType.Double],
	[CustomFieldType.AmountField]: [ConnectwiseSystemFieldType.Number],
	[CustomFieldType.Dropdown]: [ConnectwiseSystemFieldType.Dropdown],
	[CustomFieldType.SystemField]: [],
	[CustomFieldType.DropdownFieldWithKeyValue]: [],
	[CustomFieldType.CurrencyField]: [],
	[CustomFieldType.FormulaField]: [],
	[CustomFieldType.MultiSelect]: []
};

export const ZmToATCustomFieldTypeMap: Record<
	CustomFieldType,
	AutotaskCustomFieldType[]
> = {
	[CustomFieldType.DateField]: [AutotaskCustomFieldType.Date],
	[CustomFieldType.TextField]: [AutotaskCustomFieldType.String],
	[CustomFieldType.UrlField]: [AutotaskCustomFieldType.String],
	[CustomFieldType.NumberField]: [
		AutotaskCustomFieldType.Long,
		AutotaskCustomFieldType.Integer,
		AutotaskCustomFieldType.Short
	],
	[CustomFieldType.DecimalField]: [
		AutotaskCustomFieldType.Decimal,
		AutotaskCustomFieldType.Double
	],
	[CustomFieldType.AmountField]: [
		AutotaskCustomFieldType.Decimal,
		AutotaskCustomFieldType.Double
	],
	[CustomFieldType.Dropdown]: [],
	[CustomFieldType.SystemField]: [],
	[CustomFieldType.DropdownFieldWithKeyValue]: [],
	[CustomFieldType.CurrencyField]: [],
	[CustomFieldType.FormulaField]: [],
	[CustomFieldType.MultiSelect]: []
};

export interface CustomField {
	readonly id: string;
	readonly company_id: string;
	readonly created: Date;
	readonly creator_user_id: string;
	readonly deleted: boolean;
	readonly display_name: string;
	readonly field_type: CustomFieldType;
	readonly allowed_dropdown_values: string[];
	readonly allowed_dropdown_values_and_abbreviations?: [string, string][];
	readonly formula?: string;
	readonly entity_type: ZomentumEntities;
	readonly is_editable_field: boolean;
	readonly is_deletable_field: boolean;
	readonly updated: Date;

	values: string[];

	readonly mandatory_field_details: CustomFieldMandatoryFieldDetails | null;
	readonly system_field_id: string | null;

	readonly custom_field_type?: string;
	readonly allowed_dropdown_key_values?: KeyValuePair[];
	readonly is_data_editable_field?: boolean;
	readonly parent_field_info?: {
		readonly parent_id: string;
		readonly allowed_values: Record<string, string[]>;
	};
	readonly creator_user?: UserData;
	readonly partner_sharing_details?: {
		shared_with_partner: boolean;
		mandatory_for_opp_sharing: boolean;
	};
	readonly is_system_field?: boolean;
	readonly is_value_editable_from_ui?: boolean;
}

export class DynamicPricingDistributors {
	selected_custom_distributor_details?: SelectedCustomDistributorDetails;
	distributor: Distributor = Distributor.Ingram;
	selected_warehouse: string | null = null;
}

export enum DescimalRountingPrecision {
	Hundreds = 2,
	Tens = 1
}
export enum WholeNumberRountingPrecision {
	Ones = 0,
	Tens = -1,
	Hundreds = -2,
	Thousands = -3,
	Millions = -6
}

export enum RoundingType {
	Nearest = "nearest",
	HalfUp = "half_up",
	HalfDown = "half_down",
	Ceiling = "ceiling",
	Floor = "floor",
	HalfToEven = "half_to_even",
	HalfToOdd = "half_to_odd",
	HalfAwayFromZero = "half_away_from_zero",
	HalfTowardsZero = "half_towards_zero"
}

export class RoundingDetails {
	precision: DescimalRountingPrecision | WholeNumberRountingPrecision =
		WholeNumberRountingPrecision.Ones;
	is_enabled = false;
	method: RoundingType = RoundingType.Nearest;
}

export class AutoPriceUpdateDetails {
	is_enabled = false;
	selected_distributors: DynamicPricingDistributors[] =
		new Array<DynamicPricingDistributors>();
	rounding_details: RoundingDetails = new RoundingDetails();
}

export class CustomFieldCount {
	[ZomentumEntities.Address] = 0;
	[ZomentumEntities.Opportunity] = 0;
	[ZomentumEntities.Client] = 0;
	[ZomentumEntities.ClientContact] = 0;
	[ZomentumEntities.Product] = 0;
	[ZomentumEntities.Document] = 0;
	[ZomentumEntities.VendorPartner] = 0;
}

export class CustomFieldCountPartnerAlign {
	[ZomentumEntities.VendorOpportunity] = 0;
	[ZomentumEntities.VendorClientContact] = 0;
	[ZomentumEntities.VendorClient] = 0;
	[ZomentumEntities.MarketplaceSKU] = 0;
	[ZomentumEntities.VendorPartner] = 0;
	[ZomentumEntities.SalesLogCall] = 0;
}

export interface CustomFieldsResponse {
	data: CustomField[];
}

export interface ReorderCustomFieldRequest {
	fields: CustomField[];
	entityType:
		| ZomentumEntities.Opportunity
		| ZomentumEntities.Client
		| ZomentumEntities.ClientContact
		| ZomentumEntities.Document
		| ZomentumEntities.Product
		| ZomentumEntities.VendorOpportunity
		| ZomentumEntities.VendorClient
		| ZomentumEntities.VendorClient
		| ZomentumEntities.VendorClientContact
		| ZomentumEntities.MarketplaceSKU
		| ZomentumEntities.VendorPartner;
}

export interface CustomFieldFormValueEditDropdownValue {
	old_value: string | null;
	replace_with: string | null;
	is_original: boolean;
	is_modified: boolean;
}

export interface CustomFieldMandatoryFieldDetails {
	[CustomFieldMandatoryFieldDetailsValues.FOR_UI]: boolean;
	[CustomFieldMandatoryFieldDetailsValues.FOR_CSV]: boolean;
	[CustomFieldMandatoryFieldDetailsValues.FOR_ZAPIER]: boolean;
}

export enum CustomFieldMandatoryFieldDetailsValues {
	FOR_UI = "for_ui",
	FOR_CSV = "for_csv",
	FOR_ZAPIER = "for_zapier"
}

export interface CustomFieldFormValues extends CustomField {
	dropdown_values_to_add: string[];
	dropdown_values_to_update: CustomFieldFormValueEditDropdownValue[];
	dropdown_values_to_remove: CustomFieldFormValueEditDropdownValue[];
	mandatory_fields?: CustomFieldMandatoryFieldDetailsValues[] | null;
	shared_with_partner_field?: boolean;
	mandatory_for_opp_sharing_field?: boolean;
}

export interface ConnectCalendarFormValues {
	calendar_id: string;
	two_way_sync: boolean;
	sync_tasks: boolean;
	should_create_contacts_from_attendees: boolean;
	ignored_domains: string[];
}

export interface CustomEmailVerification {
	email: string;
	name?: string;
	status: DKIMStatus;
	isDefault: boolean;
}

export interface ValidateDeleteCustomFieldResponse {
	can_safely_delete: boolean;
	approval_automations: DocumentApproval[];
	reports: Report[];
	sales_automations: SalesActivityAutomation[];
}

export enum DKIMStatus {
	Pending = "pending",
	Success = "success",
	Failed = "failed",
	TemporaryFailed = "temporary_failure",
	NotStarted = "not_started"
}

export interface ConnectedEmails {
	name: string;
	email: string;
	status?: DKIMStatus;
}

export interface CustomDomain {
	dkim_enabled: boolean;
	domain: string;
	status: DKIMStatus;
	connected_emails: ConnectedEmails[];
	read_only: boolean;
	mail_from_sub_domain?: string;
}

export interface DkimVerification {
	email: CustomEmailVerification[];
	domain: CustomDomain[];
}

export interface Quantifier {
	prefix: string;
	suffix: string;
	number?: number | string;
}

export enum DatePatterns {
	MM_SLASH_DD_SLASH_YYYY = "MM/dd/yyyy",
	DD_SLASH_MM_SLASH_YYYY = "dd/MM/yyyy",
	DD_HYPHEN_MMM_COMMA_YYYY = "dd-MMM, yyyy",
	YYYY_SLASH_MM_SLASH_DD = "yyyy/MM/dd",
	MMM_HYPHEN_DD_COMMA_YYYY = "MMM-dd, yyyy"
}

export const months = [
	"January",
	"February",
	"March",
	"April",
	"May",
	"June",
	"July",
	"August",
	"September",
	"October",
	"November",
	"December"
];

export enum KaseyaURL {
	bms = "bms",
	bmsemea = "bmsemea",
	bmsapac = "bmsapac",
	custom_url = "custom_url"
}

export const KaseyaURLHost = {
	[KaseyaURL.bms]: "https://bms.kaseya.com/Login.aspx",
	[KaseyaURL.bmsemea]: "https://bmsemea.kaseya.com/Login.aspx",
	[KaseyaURL.bmsapac]: "https://bmsapac.kaseya.com/Login.aspx"
};

export interface KaseyaConnectFormValues {
	url: KaseyaURL;
	server_endpoint: string;
	company: string;
	username: string;
	password: string;
}

export interface KaseyaProductSyncDetails {
	pull_update_on_products: boolean;
}
export interface KaseyaSettingsFormValues {
	mode: IntegrationMode;
	sync_option: SyncOption;
	push_quotations: boolean;
	product_sync_detail: boolean;
	pull_updates: boolean;
	quote_status: string;
	warehouse: string;
	billing_period_for_labor_item: string;
	product_category: string;
}

export class StringifiedLabelValuePair {
	label: number | string = 0;
	value = "";
	constructor(label: number | string, value: string) {
		this.label = label;
		this.value = value;
	}
}

export interface KaseyaFieldMappingFormValues {
	readonly bundle_push_mode: KaseyaPushBundleLevelOption;
	readonly pipeline_stage_id: string;
	readonly kaseya_opportunity_status: string;
	readonly quote_status: string;
	readonly multiple_pipeline_pull_mapping?: KaseyaOpportunityPullPipelineMappingFormValue[];
	readonly multiple_pipeline_push_mapping?: KaseyaOpportunityPushPipelineMappingFormValue[];
	readonly default_multi_pipeline_pull_mapping?: string[];
	readonly default_multi_pipeline_push_mapping?: string;
	readonly stage_mapping: Record<
		string,
		KaseyaOpportunityStageMappingFormValue
	>;
	readonly status_mapping: Record<string, KaseyaQuoteStatusMappingFormValue>;
	readonly product_export_mapping: KaseyaMappingFormValue[];
	readonly product_import_mapping: KaseyaMappingFormValue[];
}

export interface KaseyaOpportunityPullPipelineMappingFormValue {
	readonly kaseya_opportunity_status: CommonKeyValuePair | null;
	readonly zomentum_pipeline_with_pipeline_stage: string[] | null;
}

export interface KaseyaOpportunityPushPipelineMappingFormValue {
	readonly kaseya_opportunity_status: string | null;
	readonly zomentum_pipeline_with_pipeline_stage: ZomentumPipelineStageValue | null;
}

export interface KaseyaOpportunityPipelineMapping {
	readonly kaseya_opportunity_status: CommonKeyValuePair | null;
	readonly zomentum_pipeline_with_pipeline_stage: ZomentumPipelineStageValue | null;
}

export interface ZomentumPipelineStageValue {
	readonly zomentum_pipeline_id: string;
	readonly zomentum_pipeline_stage_id: string;
}

export interface KaseyaOpportunityStageMappingFormValue {
	readonly opportunity_stage: string;
}

export interface KaseyaQuoteStatusMappingFormValue {
	readonly quote_status: string;
}

export interface KaseyaMappingFormValue {
	readonly value: string;
}

export interface KaseyaConnectStepRequest extends KaseyaCredentialsTestRequest {
	readonly url: KaseyaURL;
}

export class KaseyaCredentialsTestRequest {
	server_endpoint: string | null = "";
	company: string | null = "";
	username: string | null = "";
	password: string | null = "";

	constructor(args: {
		server_endpoint: string | null;
		company: string | null;
		username: string | null;
		password: string | null;
	}) {
		this.server_endpoint = args.server_endpoint;
		this.company = args.company;
		this.username = args.username;
		this.password = args.password;
	}
}

export class KaseyaSettingsRequest {
	mode: IntegrationMode = IntegrationMode.CRM;
	two_way_sync = false;
	push_quotations = false;
	pull_updates = false;
	product_sync_detail: KaseyaProductSyncDetails | null = null;
	default_values?: KaseyaDefaultValues = new KaseyaDefaultValues();
}

export class KaseyaFieldMappingRequest {
	bundle_push_mode?: KaseyaPushBundleLevelOption;
	pipeline_stage_id?: string;
	pipeline_id?: string;
	kaseya_opportunity_status?: CommonKeyValuePair;
	quote_status?: CommonKeyValuePair;
	multiple_pipeline_pull_mapping?: KaseyaOpportunityPipelineMapping[] | null;
	multiple_pipeline_push_mapping?: KaseyaOpportunityPipelineMapping[] | null;
	opportunity_status_mappings?: KaseyaOpportunityStatusMapping[] | null;
	document_to_kaseya_quote_mappings?: KaseyaDocumentQuoteMapping[] | null;
	product_import_mapping?: KaseyaProductMapping | null;
	product_export_mapping?: KaseyaProductMapping | null;
}

export interface KaseyaOpportunityStatusMapping {
	pipeline_stage_id: string;
	kaseya_opportunity_status?: CommonKeyValuePair;
	opportunity_status?: CommonKeyValuePair;
}

export interface KaseyaDocumentQuoteMapping {
	document_status: string;
	kaseya_quote_status?: CommonKeyValuePair;
}

export class KaseyaDefaultValues {
	pipeline_stage_id?: string;
	pipeline_id?: string;
	quote_status?: CommonKeyValuePair;
	warehouse?: CommonKeyValuePair;
	billing_period_for_labor_item?: CommonKeyValuePair;
	product_category?: CommonKeyValuePair;
	kaseya_opportunity_status?: CommonKeyValuePair;
}

export interface KaseyaProductFieldMappingValue {
	readonly label: string;
	readonly value: string;
}

export interface KaseyaProductFieldMapping {
	readonly mapped_by_label_value: KaseyaProductFieldMappingValue;
	readonly mapped_to_label_value: KaseyaProductFieldMappingValue;
	readonly mapped_to_label_values: KaseyaProductFieldMappingValue[];
}

export interface KaseyaProductMapping {
	readonly mapped_by?: string | null;
	readonly mapped_to?: string | null;
	readonly fields?: KaseyaProductFieldMapping[] | null;
}

export interface KaseyaCredentialsTestResponse {
	readonly existing?: KaseyaIntegration | null;
	readonly kaseya_opportunity_status: CommonKeyValuePair[];
	readonly pipeline: Pipeline | null;
	readonly pipelines: Pipeline[];
	readonly document_status: DocumentStatus[];
	readonly kaseya_quote_status: CommonKeyValuePair[];
	readonly kaseya_product_categories: CommonKeyValuePair[];
	readonly billing_periods_for_labor_item: CommonKeyValuePair[];
	readonly multiple_pipeline_pull_mapping?:
		| KaseyaOpportunityPipelineMapping[]
		| null;
	readonly multiple_pipeline_push_mapping?:
		| KaseyaOpportunityPipelineMapping[]
		| null;
	readonly warehouses: CommonKeyValuePair[];
	readonly product_import_mapping: KaseyaProductMapping | null;
	readonly product_export_mapping: KaseyaProductMapping | null;
}

export enum KaseyaPushBundleLevelOption {
	PUSH_AS_SINGLE_SERVICE = "push_as_single_service",
	PUSH_AS_SERVICE_WITH_CHILD_ITEM_ZERO_PRICE = "push_as_service_with_child_item_zero_price",
	INDIVIDUAL_CHILD_ITEM_WITH_BUNDLE_PRICE_EQUALLY_DIVIDED = "individual_child_item_with_bundle_price_equally_divided"
}

export interface AddKaseyaIntegrationRequest {
	username?: string | null;
	password?: string | null;
	company?: string | null;
	server_endpoint?: string | null;
	two_way_sync?: boolean;
	mode?: IntegrationMode;
	push_quotations?: boolean;
	multiple_pipeline_pull_mapping?: KaseyaOpportunityPipelineMapping[] | null;
	multiple_pipeline_push_mapping?: KaseyaOpportunityPipelineMapping[] | null;
	product_sync_detail?: KaseyaProductSyncDetails | null;
	pull_updates?: boolean;
	bundle_push_mode?: KaseyaPushBundleLevelOption;
	default_values?: KaseyaDefaultValues;
	opportunity_status_mappings?: KaseyaOpportunityStatusMapping[] | null;
	document_to_kaseya_quote_mappings?: KaseyaDocumentQuoteMapping[] | null;
	product_import_mapping?: KaseyaProductMapping | null;
	product_export_mapping?: KaseyaProductMapping | null;
}

export interface KaseyaErrorObject {
	error: string;
	error_description: string;
}

export enum DickerDataConnectionOptions {
	API = "API Feed",
	CSV = "CSV Feed"
}

export enum DickerDataDistributionType {
	API = "via_api",
	CSV = "via_url"
}

export class RepairShoprCredentialsTestRequest {
	api_key = "";
	sub_domain = "";
	constructor(args: { api_key: string; sub_domain: string }) {
		this.api_key = args.api_key;
		this.sub_domain = args.sub_domain;
	}
}

export interface RepairShoprProductCategoryView {
	id: number;
	account_id: number;
	ancestry?: string;
	name?: string;
	description?: string;
	device_product_id?: string;
	names_depth_cache: string;
	effective_name: string;
}

export interface RepairShoprCredentailsTestResponse {
	pipeline: Pipeline;
	product_categories: RepairShoprProductCategoryView[];
	existing: SyncroIntegration | null;
}

export interface RepairShoprConnectFormValues {
	api_key: string;
	sub_domain: string;
}

export interface RepairShoprSettingsFormValues {
	sync_option: SyncOption;
	client_created_at_filter?: Date;
	should_import_product?: boolean;
	product_category_to_import?: string[];
	should_import_client_hierarchy: boolean;
	should_split_line_items: boolean;
}

export class RepairShoprSettingsRequest {
	sync_option: SyncOption = SyncOption.ExportOnly;
	client_created_at_filter?: Date;
	should_import_product?: boolean;
	product_category_to_import?: RepairShoprProductCategoryView[] | null;
	should_import_client_hierarchy = false;
	should_split_line_items = false;
}
export interface RepairShoprProductMapping {
	zomentum_product_type: ProductType;
	product_category?: RepairShoprProductCategoryView;
}
export class RepairShoprFieldMappingRequest {
	product_mapping: RepairShoprProductMapping[] =
		new Array<RepairShoprProductMapping>();
	generate_fields = false;
}

export class StringifyRepairShoprProduct {
	id: number;
	account_id: number;
	ancestry?: string;
	name?: string;
	description?: string;
	device_product_id?: string;
	names_depth_cache?: string;
	constructor(args: {
		id: number;
		account_id: number;
		ancestry?: string;
		name?: string;
		description?: string;
		device_product_id?: string;
		names_depth_cache?: string;
	}) {
		this.id = args.id;
		this.account_id = args.account_id;
		this.ancestry = args.ancestry;
		this.name = args.name;
		this.description = args.description;
		this.device_product_id = args.device_product_id;
		this.names_depth_cache = args.names_depth_cache;
	}
}

export class AddRepairShoprIntegrationRequest {
	api_key?: string;
	sub_domain?: string;
	create_entities_as_user_id?: string;
	sync_option?: SyncOption;
	should_import_client_hierarchy?: boolean;
	should_import_product?: boolean;
	client_created_at_filter: Date | null = null;
	product_category_to_import?: RepairShoprProductCategoryView[] | null = null;
	product_mapping?: RepairShoprProductMapping[] =
		new Array<RepairShoprProductMapping>();
	should_split_line_items?: boolean;
	generate_product_category_custom_field = false;
}

export enum HubspotEntities {
	COMPANY = "company",
	DEAL = "deal",
	CONTACT = "contact",
	PARTNER = "partner",
	CLIENT = "client"
}

export enum HubspotActivities {
	NOTES = "notes",
	CALLS = "calls",
	TASKS = "tasks"
}

export enum HubspotPushModes {
	DONT_PUSH = "dont_push",
	WHEN_OPPORTUNITY_CREATE_OR_STAGE_UPDATED = "when_opportunity_create_or_stage_updated",
	WHEN_OPPORTUNITY_MOVED_TO_TERMINAL_STAGE = "when_opportunity_moved_to_terminal_stage"
}

export interface HubspotCompanyPullFiltersFormValues
	extends Partial<HubspotCompanyPullFiltersRequest> {
	readonly company_with_lifecycle_stage_checkbox: boolean;
	readonly company_with_lead_status_checkbox: boolean;
	readonly company_with_type_checkbox: boolean;
}

export enum HubspotCompanyPullFiltersFormKeys {
	HubspotCompanyPullFilters = "hubspot_company_pull_filters",
	CompanyWithLifecycleStage = "company_with_lifecycle_stage",
	CompanyWithLifecycleStageCheckbox = "company_with_lifecycle_stage_checkbox",
	CompanyWithLeadStatus = "company_with_lead_status",
	CompanyWithLeadStatusCheckbox = "company_with_lead_status_checkbox",
	CompanyWithType = "company_with_type",
	CompanyWithTypeCheckbox = "company_with_type_checkbox"
}

export interface HubspotDealPullFiltersFormValues
	extends Partial<HubspotDealPullFiltersRequest> {
	readonly deal_with_owner_id_checkbox: boolean;
	readonly deal_with_stage_id_checkbox: boolean;
	readonly deal_with_type_checkbox: boolean;
}

export enum HubspotDealPullFiltersFormKeys {
	HubspotDealPullFilters = "hubspot_deal_pull_filters",
	DealWithOwnerId = "deal_with_owner_id",
	DealWithOwnerIdCheckbox = "deal_with_owner_id_checkbox",
	DealWithStageId = "deal_with_stage_id",
	DealWithStageIdCheckbox = "deal_with_stage_id_checkbox",
	DealWithType = "deal_with_type",
	DealWithTypeCheckbox = "deal_with_type_checkbox"
}

export interface HubspotContactPullFiltersFormValues
	extends Partial<HubspotContactPullFiltersRequest> {
	readonly contact_with_lifecycle_stage_checkbox: boolean;
	readonly contact_with_lead_status_checkbox: boolean;
}

export interface HubspotSettingsFormValues {
	readonly activities?: HubspotActivities[];
	readonly hubspot_company_pull_filters?: HubspotCompanyPullFiltersFormValues;
	readonly hubspot_deal_pull_filters?: HubspotDealPullFiltersFormValues;
	readonly hubspot_contact_pull_filters?: HubspotContactPullFiltersFormValues;
	readonly hubspot_default_pull_entities?: HubspotDefaultPullEntities;
	readonly opportunity_push?: HubspotPushModes;
	readonly pull_selects?: HubspotEntities[];
	readonly push_quote_as_attachment?: boolean;
	readonly pull_company_selects?: IntgerationPullSelectOption;
	readonly pull_opportunity_selects?: IntgerationPullSelectOption;
	readonly when_client_created?: boolean;
	readonly when_opportunity_created?: boolean;
	readonly should_push_client_and_opportunity?: boolean;
}

export interface VendorHubspotSettingsFormValues
	extends Omit<
		HubspotSettingsFormValues,
		| "hubspot_company_pull_filters"
		| "hubspot_contact_pull_filters"
		| "push_quote_as_attachment"
	> {
	readonly hubspot_client_company_pull_filters: HubspotCompanyPullFiltersFormValues;
	readonly hubspot_client_contact_pull_filters: HubspotContactPullFiltersFormValues;
}

export class HubspotOpportunityPushSettings {
	when_opportunity_created = false;
	when_opportunity_moved_to_terminal_stage = false;
}

export class HubspotClientPushSettings {
	when_client_created = false;
	when_opportunity_getting_pushed = false;
}

export class HubspotPushSettingsRequest {
	when_opportunity_created_or_stage_updated = false;
	when_opportunity_moved_to_terminal_stage = false;
	push_quote_as_attachment: boolean | undefined = undefined;
	opportunity_push_settings: HubspotOpportunityPushSettings | null = null;
	client_push_settings: HubspotClientPushSettings | null = null;

	public static isPushingClientAndOpportunity = (
		clientPushSettings?: HubspotClientPushSettings | null,
		opportunityPushSettings?: HubspotOpportunityPushSettings | null
	) => {
		return (
			!!clientPushSettings?.when_client_created ||
			!!clientPushSettings?.when_opportunity_getting_pushed ||
			!!opportunityPushSettings?.when_opportunity_created ||
			!!opportunityPushSettings?.when_opportunity_moved_to_terminal_stage
		);
	};
}

export class HubspotCompanyPullFiltersRequest {
	company_with_lifecycle_stage: string[] | null = null;
	company_with_lead_status: string[] | null = null;
	company_with_type: string[] | null = null;
	company_with_deal_attached = false;
	company_with_partner_manager = false;

	public static getHubspotCompanyPullRequest = (
		companyFormValues?: HubspotCompanyPullFiltersFormValues
	): HubspotCompanyPullFiltersRequest => {
		const hubspotCompanyPullRequest =
			new HubspotCompanyPullFiltersRequest();

		hubspotCompanyPullRequest.company_with_lifecycle_stage =
			companyFormValues?.company_with_lifecycle_stage_checkbox
				? companyFormValues.company_with_lifecycle_stage ?? null
				: null;
		hubspotCompanyPullRequest.company_with_lead_status =
			companyFormValues?.company_with_lead_status_checkbox
				? companyFormValues.company_with_lead_status ?? null
				: null;
		hubspotCompanyPullRequest.company_with_type =
			companyFormValues?.company_with_type_checkbox
				? companyFormValues.company_with_type ?? null
				: null;
		hubspotCompanyPullRequest.company_with_deal_attached =
			companyFormValues?.company_with_deal_attached ?? false;
		hubspotCompanyPullRequest.company_with_partner_manager =
			companyFormValues?.company_with_partner_manager ?? false;

		return hubspotCompanyPullRequest;
	};
}

export class HubspotDealPullFiltersRequest {
	deal_with_owner_id: string[] | null = null;
	deal_with_stage_id: string[] | null = null;
	deal_with_type: string[] | null = null;
	deal_attached_with_company_or_contact = false;
	deal_with_partner_manager = false;

	public static getHubspotDealPullRequest = (
		dealFormValues?: HubspotDealPullFiltersFormValues
	): HubspotDealPullFiltersRequest => {
		const hubspotDealPullRequest = new HubspotDealPullFiltersRequest();

		hubspotDealPullRequest.deal_with_owner_id =
			dealFormValues?.deal_with_owner_id_checkbox
				? dealFormValues.deal_with_owner_id ?? null
				: null;
		hubspotDealPullRequest.deal_with_stage_id =
			dealFormValues?.deal_with_stage_id_checkbox
				? dealFormValues.deal_with_stage_id ?? null
				: null;
		hubspotDealPullRequest.deal_with_type =
			dealFormValues?.deal_with_type_checkbox
				? dealFormValues.deal_with_type ?? null
				: null;
		hubspotDealPullRequest.deal_attached_with_company_or_contact =
			dealFormValues?.deal_attached_with_company_or_contact ?? false;
		hubspotDealPullRequest.deal_with_partner_manager =
			dealFormValues?.deal_with_partner_manager ?? false;

		return hubspotDealPullRequest;
	};
}

export class HubspotContactPullFiltersRequest {
	contact_with_lifecycle_stage: string[] | null = null;
	contact_with_lead_status: string[] | null = null;
	contact_attached_with_company_or_deal = false;

	public static getHubspotContactPullRequest = (
		contactFormValues?: HubspotContactPullFiltersFormValues
	): HubspotContactPullFiltersRequest => {
		const hubspotContactPullRequest =
			new HubspotContactPullFiltersRequest();

		hubspotContactPullRequest.contact_with_lifecycle_stage =
			contactFormValues?.contact_with_lifecycle_stage_checkbox
				? contactFormValues.contact_with_lifecycle_stage ?? null
				: null;
		hubspotContactPullRequest.contact_with_lead_status =
			contactFormValues?.contact_with_lead_status_checkbox
				? contactFormValues.contact_with_lead_status ?? null
				: null;
		hubspotContactPullRequest.contact_attached_with_company_or_deal =
			contactFormValues?.contact_attached_with_company_or_deal ?? false;

		return hubspotContactPullRequest;
	};
}

export interface DefaultHubsptStage {
	hubspot_pipeline_id: string;
	hubspot_stage_id: string;
}

export interface DefaultZomentumStage {
	zomentum_pipeline_id: string;
	zomentum_stage_id: string;
}

export class HubspotDefaultPullEntities {
	default_client_company_for_deal: string | null = null;
	default_client_company_for_contact: string | null = null;
	default_owner_for_company: string | null = null;
	default_owner_for_deal: string | null = null;
	default_owner_for_activities: string | null = null;
	default_hubspot_pipeline_details: DefaultHubsptStage | null = {
		hubspot_pipeline_id: "",
		hubspot_stage_id: ""
	};
	default_zomentum_pipeline_details?: DefaultZomentumStage | null = {
		zomentum_pipeline_id: "",
		zomentum_stage_id: ""
	};

	public static getHubspotDefaultPullEntitiesRequest = (
		defaultFormValues?: HubspotDefaultPullEntities
	): HubspotDefaultPullEntities => {
		const hubspotDefaultPullEntitiesRequest =
			new HubspotDefaultPullEntities();

		hubspotDefaultPullEntitiesRequest.default_client_company_for_deal =
			defaultFormValues?.default_client_company_for_deal ?? null;
		hubspotDefaultPullEntitiesRequest.default_client_company_for_contact =
			defaultFormValues?.default_client_company_for_contact ?? null;
		hubspotDefaultPullEntitiesRequest.default_owner_for_company =
			defaultFormValues?.default_owner_for_company ?? null;
		hubspotDefaultPullEntitiesRequest.default_owner_for_deal =
			defaultFormValues?.default_owner_for_deal ?? null;
		hubspotDefaultPullEntitiesRequest.default_owner_for_activities =
			defaultFormValues?.default_owner_for_activities ?? null;
		hubspotDefaultPullEntitiesRequest.default_hubspot_pipeline_details =
			defaultFormValues?.default_hubspot_pipeline_details ?? null;
		hubspotDefaultPullEntitiesRequest.default_zomentum_pipeline_details =
			defaultFormValues?.default_zomentum_pipeline_details ?? null;

		return hubspotDefaultPullEntitiesRequest;
	};
}

export class HubspotPullSettingsRequest {
	pull_deal = false;
	pull_contact = false;
	pull_note = false;
	pull_call = false;
	pull_task = false;
	hubspot_company_pull_filters: HubspotCompanyPullFiltersRequest | null =
		new HubspotCompanyPullFiltersRequest();
	hubspot_deal_pull_filters: HubspotDealPullFiltersRequest | null = null;
	hubspot_contact_pull_filters: HubspotContactPullFiltersRequest | null =
		null;
	hubspot_default_pull_entities: HubspotDefaultPullEntities | null =
		new HubspotDefaultPullEntities();
}

export class VendorHubspotPullSettingsRequest {
	pull_deal = false;
	pull_partner_contact = false;
	pull_client_contact = false;
	pull_note = true;
	pull_call = false;
	pull_task = false;
	hubspot_client_company_pull_filters: HubspotCompanyPullFiltersRequest | null =
		new HubspotCompanyPullFiltersRequest();
	hubspot_client_contact_pull_filters: HubspotContactPullFiltersRequest | null =
		null;
	hubspot_deal_pull_filters: HubspotDealPullFiltersRequest | null = null;
	hubspot_default_pull_entities: HubspotDefaultPullEntities | null =
		new HubspotDefaultPullEntities();
}

export class HubspotStageDetails {
	hubspot_stage_id = "";
	label = "";
	constructor(id: string | number, label: string) {
		this.hubspot_stage_id = String(id);
		this.label = label;
	}
}

export class ZomentumStageDetails {
	zomentum_stage_id = "";
	label = "";
	constructor(id: string | number, label: string) {
		this.zomentum_stage_id = String(id);
		this.label = label;
	}
}

export class HubspotPipelineStageMappingRequest {
	hubspot_pipeline_id = "";
	zomentum_stage_details: ZomentumStageDetails = new ZomentumStageDetails(
		"",
		""
	);
	hubspot_stage_details: HubspotStageDetails = new HubspotStageDetails(
		"",
		""
	);
}

export interface HubspotFieldMappingFormValues {
	readonly pipeline_mapping: Record<string, Record<string, string[]>>;
	readonly opportunity_field_mapping: Record<string, string>;
	readonly client_company_field_mapping: Record<string, string>;
	readonly client_user_field_mapping: Record<string, string>;
	readonly default_hubspot_pipeline_details: string[];
	readonly default_zomentum_pipeline_details: string[];
}

export class HubspotOpportunityPipelineMappingRequest {
	zomentum_pipeline_id = "";
	hubspot_pipeline_stage_mapping: HubspotPipelineStageMappingRequest[] =
		new Array<HubspotPipelineStageMappingRequest>();
}

export interface HubspotFieldMappingFormRequest
	extends Pick<
		AddHubspotIntegrationRequest,
		| "hubspot_opportunity_pipeline_mapping"
		| "client_company_field_mapping"
		| "client_user_field_mapping"
		| "opportunity_field_mapping"
	> {
	default_hubspot_pipeline_details: DefaultHubsptStage;
	default_zomentum_pipeline_details?: DefaultZomentumStage;
}

export class AddHubspotIntegrationRequest {
	hubspot_push_settings: HubspotPushSettingsRequest | null = null;
	hubspot_pull_settings: HubspotPullSettingsRequest | null = null;
	hubspot_opportunity_pipeline_mapping: HubspotOpportunityPipelineMappingRequest[] =
		new Array<HubspotOpportunityPipelineMappingRequest>();
	opportunity_field_mapping: HubspotFieldMappingRequest[] =
		new Array<HubspotFieldMappingRequest>();
	client_company_field_mapping: HubspotFieldMappingRequest[] =
		new Array<HubspotFieldMappingRequest>();
	client_user_field_mapping: HubspotFieldMappingRequest[] =
		new Array<HubspotFieldMappingRequest>();
}

export class AddVendorHubspotIntegrationRequest {
	hubspot_push_settings: HubspotPushSettingsRequest | null = null;
	hubspot_pull_settings: VendorHubspotPullSettingsRequest | null = null;
	hubspot_opportunity_pipeline_mapping: HubspotOpportunityPipelineMappingRequest[] =
		new Array<HubspotOpportunityPipelineMappingRequest>();
	opportunity_field_mapping: HubspotFieldMappingRequest[] =
		new Array<HubspotFieldMappingRequest>();
	client_company_field_mapping: HubspotFieldMappingRequest[] =
		new Array<HubspotFieldMappingRequest>();
	client_user_field_mapping: HubspotFieldMappingRequest[] =
		new Array<HubspotFieldMappingRequest>();
}

export interface HubspotMetadataFieldOptions {
	readonly field_id: string | number;
	readonly display_name: string;
	readonly is_owner_field?: boolean;
}

export interface HubspotMetadataOwnerFieldOptions
	extends HubspotMetadataFieldOptions {
	readonly email: string;
}

export interface HubspotFieldMappingRequest {
	readonly zomentum_field: string;
	readonly hubspot_field: string;
}

export interface ZomentumToHubspotFieldMapping {
	zomentum_field: string;
	zomentum_label: string;
	hubspot_field: string | null;
	hubspot_label: string | null;
}

export interface HubspotFieldMapping {
	default_mapping: Omit<ZomentumToHubspotFieldMapping, "zomentum_field">[];
	user_mapping: ZomentumToHubspotFieldMapping[];
}

export interface HubspotPipelineResponse {
	readonly pipeline_id: string;
	readonly pipeline_name: string;
	readonly pipeline_stages: HubspotMetadataFieldOptions[];
}

export interface HubspotMetadataResponse {
	readonly hubspot_company_properties: HubspotMetadataFieldOptions[];
	readonly hubspot_deal_properties: HubspotMetadataFieldOptions[];
	readonly hubspot_contact_properties: HubspotMetadataFieldOptions[];
	readonly opportunity_pipelines: HubspotPipelineResponse[];
	readonly deal_pipelines: HubspotPipelineResponse[];
	readonly hubspot_users: HubspotMetadataOwnerFieldOptions[];
	readonly hubspot_company_types: HubspotMetadataFieldOptions[];
	readonly hubspot_deal_types: HubspotMetadataFieldOptions[];
	readonly hubspot_lead_status_types: HubspotMetadataFieldOptions[];
	readonly hubspot_company_lead_status_types: HubspotMetadataFieldOptions[];
	readonly hubspot_contact_lead_status_types: HubspotMetadataFieldOptions[];
	readonly hubspot_lifecycle_stage_types: HubspotMetadataFieldOptions[];
	readonly opportunity_field_mapping: HubspotFieldMapping;
	readonly client_company_field_mapping: HubspotFieldMapping;
	readonly client_user_field_mapping: HubspotFieldMapping;
}

export interface VendorHubspotMetadataResponse
	extends Omit<
		HubspotMetadataResponse,
		| "hubspot_company_properties"
		| "hubspot_contact_properties"
		| "opportunity_pipelines"
	> {
	readonly hubspot_client_company_properties: HubspotMetadataFieldOptions[];
	readonly hubspot_client_contact_properties: HubspotMetadataFieldOptions[];
}

export class HubspotIntegration extends AddHubspotIntegrationRequest {
	connected_user = "";
	hubspot_user_id: string | number = "";
	hubspot_hub_id: string | number = "";
	hubspot_app_id: string | number = "";
	is_initial_sync_pending = false;
	block_negative_quantity?: boolean | null;
	block_zero_quantity?: boolean | null;
}

export class VendorHubspotIntegration extends AddVendorHubspotIntegrationRequest {
	is_initial_sync_pending = false;
	connected_user = "";
}

export const hubspotSelectedLifecycleStagesIds = [
	"opportunity",
	"customer",
	"salesqualifiedlead",
	"evangelist"
];

export const hubspotSelectedCompanyIds = ["PROSPECT", "VENDOR"];

export interface StripeUser {
	account_id: string;
	details_submitted: boolean;
	charges_enabled: boolean;
}

export class QuickbooksIntegration {
	connected_user = "";
	tax_mappings: QuickbooksTaxMappings[] | null = null;
	default_values: QuickbooksDefaultValues | null = null;
	convert_estimates_to_invoices_on_document_won = true;
	is_initial_sync_pending = false;
	block_negative_quantity?: boolean | null;
	block_zero_quantity?: boolean | null;
}

export interface QuickbooksTaxMapping {
	readonly region_id: string;
	readonly region_name: string;
	readonly category_id: string;
	readonly category_name: string;
	readonly percentage: number;
}
export interface QuickbooksTaxInfoList {
	readonly tax_code_id: string;
	readonly tax_code_name: string;
	readonly tax_rate: number;
}
export class QuickbooksConnectedUser {
	connected_user = "";
	tax_mappings: QuickbooksTaxMappings[] = new Array<QuickbooksTaxMappings>();
	default_values: QuickbooksDefaultValues | null = null;
	sync_quotes = false;
	sync_payments = false;
	convert_estimates_to_invoices_on_document_won = false;
	zm_item_type_and_billing_period_to_qbo_account_mappings: ProductTypeToQuickbooksAccountMappingRequest[] =
		new Array<ProductTypeToQuickbooksAccountMappingRequest>();
	allowed_zm_payment_mode: ZomentumPaymentMode[] = [];
	zm_payment_mode_to_qbo_payment_method_mappings: ZomentumPaymentModeToQuickbooksPaymentMapping[] =
		[];
	zm_payment_mode_to_qbo_deposit_account_mappings: ZomentumPaymentModeToQuickbooksDepositAccountMapping[] =
		[];
}

export interface QuickbooksTaxInfo {
	readonly zomentum_tax_info_list: QuickbooksTaxMapping[];
	readonly quickbooks_tax_info_list: QuickbooksTaxInfoList[];
}

export interface CommonNameValuePair {
	readonly name: string;
	readonly value: string;
}

export interface QuickbooksCustomerResponse {
	readonly customers: CommonNameValuePair[];
}
export interface QuickbooksMetadataResponse {
	readonly existing: QuickbooksConnectedUser;
	readonly tax_info: QuickbooksTaxInfo;
	readonly payment_deposit_to_accounts: CommonNameValuePair[];
	readonly quickbooks_payment_methods: CommonNameValuePair[];
	readonly quickbooks_accounts: CommonNameValuePair[];
	readonly show_tax_mappings: boolean;
	readonly country?: string;
}

export interface QuickbooksTaxMappings {
	readonly region_id: string;
	readonly category_id: string;
	readonly tax_code_id: string;
}
export interface QuickbooksTaxCode {
	readonly tax_code_id: string;
}

export interface QuickbooksDefaultValues {
	readonly quickbooks_tax_info: QuickbooksTaxCode | null;
	readonly push_mode: string;
	readonly income_account: CommonNameValuePair;
	readonly qbo_deposit_account: CommonNameValuePair;
	readonly qbo_payment_method: CommonNameValuePair;
}

export interface ZomentumPaymentMode {
	gateway: Gateway;
	payment_method: AvailablePaymentMethods;
	label: string;
}

export interface ZomentumPaymentModeToQuickbooksPaymentMapping {
	zm_payment_mode: Omit<ZomentumPaymentMode, "label">;
	qbo_payment_method: CommonNameValuePair;
}
export interface ZomentumPaymentModeToQuickbooksDepositAccountMapping {
	zm_payment_mode: Omit<ZomentumPaymentMode, "label">;
	qbo_deposit_account: CommonNameValuePair;
}

export interface AddQuickbooksFormData {
	readonly convert_estimates_to_invoices_on_document_won: boolean;
	readonly income_account: string;
	readonly push_mode: string;
	readonly tax_mappings?: Record<string, Record<string, string>>;
	readonly quickbooks_tax_info?: string;
	readonly income_account_mapping: Record<
		Exclude<ProductType, ProductType.Bundle>,
		Record<QuickbooksProductBillingPeriodForMapping, string>
	>;
	readonly zm_payment_mode_to_qbo_payment_method_mappings: Record<
		Gateway,
		Record<AvailablePaymentMethods, string>
	>;
	readonly zm_payment_mode_to_qbo_deposit_account_mappings: Record<
		Gateway,
		Record<AvailablePaymentMethods, string>
	>;
	readonly push_quotes_as_estimates: boolean;
	readonly push_payments_for_quotes: boolean;
	readonly qbo_deposit_account: string;
	readonly qbo_payment_method: string;
}

export const generateProductTypeToQuickbooksAccountMappingRequest = (
	income_account_mapping: AddQuickbooksFormData["income_account_mapping"]
): ProductTypeToQuickbooksAccountMappingRequest[] => {
	try {
		const productTypeToQuickbooksAccountMappingRequest: ProductTypeToQuickbooksAccountMappingRequest[] =
			new Array<ProductTypeToQuickbooksAccountMappingRequest>();

		for (const productType in income_account_mapping) {
			const mappings: BillingPeriodToQuickbooksAccountMappingRequest[] =
				new Array<BillingPeriodToQuickbooksAccountMappingRequest>();

			const incomeAccountMappingAtProductType =
				income_account_mapping[
					productType as Exclude<ProductType, ProductType.Bundle>
				];

			for (const billingPeriod in incomeAccountMappingAtProductType) {
				const selectedAccount =
					incomeAccountMappingAtProductType[
						billingPeriod as QuickbooksProductBillingPeriodForMapping
					];
				if (selectedAccount) {
					mappings.push({
						is_recurring:
							billingPeriod ===
							QuickbooksProductBillingPeriodForMapping.Recurring,
						income_account: JSON.parse(selectedAccount)
					});
				}
			}

			!!mappings.length &&
				productTypeToQuickbooksAccountMappingRequest.push({
					item_type: productType as Exclude<
						ProductType,
						ProductType.Bundle
					>,
					mappings
				});
		}

		return productTypeToQuickbooksAccountMappingRequest;
	} catch (error) {
		console.error(
			"Error in generating product type to quickbooks account mapping request",
			error
		);
		return [];
	}
};

export const generateZMPaymentModeToQuickbooksMappingRequest = (
	mapping: Record<Gateway, Record<AvailablePaymentMethods, string>>,
	isPaymentMethodMapping = true
):
	| ZomentumPaymentModeToQuickbooksDepositAccountMapping[]
	| ZomentumPaymentModeToQuickbooksPaymentMapping[] => {
	const zmPaymentModeToQuickbooksPaymentMappingRequest: ZomentumPaymentModeToQuickbooksPaymentMapping[] =
		new Array<ZomentumPaymentModeToQuickbooksPaymentMapping>();

	const zmPaymentModeToQuickbooksDepositAccountMappingRequest: ZomentumPaymentModeToQuickbooksDepositAccountMapping[] =
		new Array<ZomentumPaymentModeToQuickbooksDepositAccountMapping>();

	try {
		for (const gateway in mapping) {
			const paymentMethodMapping = mapping[gateway as Gateway];

			for (const paymentMethod in paymentMethodMapping) {
				const selectedAccount =
					paymentMethodMapping[
						paymentMethod as AvailablePaymentMethods
					];
				if (selectedAccount) {
					const zmPaymentMode = {
						gateway: gateway as Gateway,
						payment_method: paymentMethod as AvailablePaymentMethods
					};
					const parsedSelectedAccount = JSON.parse(selectedAccount);
					if (isPaymentMethodMapping) {
						zmPaymentModeToQuickbooksPaymentMappingRequest.push({
							zm_payment_mode: zmPaymentMode,
							qbo_payment_method: parsedSelectedAccount
						});
					} else {
						zmPaymentModeToQuickbooksDepositAccountMappingRequest.push(
							{
								zm_payment_mode: zmPaymentMode,
								qbo_deposit_account: parsedSelectedAccount
							}
						);
					}
				}
			}
		}

		return isPaymentMethodMapping
			? zmPaymentModeToQuickbooksPaymentMappingRequest
			: zmPaymentModeToQuickbooksDepositAccountMappingRequest;
	} catch (error) {
		console.error(
			"Error in generating payment mode to quickbooks payment method mapping",
			error
		);
		return [];
	}
};

export interface BillingPeriodToQuickbooksAccountMappingRequest {
	is_recurring: boolean;
	income_account: CommonNameValuePair;
}

export interface ProductTypeToQuickbooksAccountMappingRequest {
	item_type: Exclude<ProductType, ProductType.Bundle>;
	mappings: BillingPeriodToQuickbooksAccountMappingRequest[];
}

export class AddQuickbooksIntegrationRequest {
	tax_mappings: QuickbooksTaxMappings[] | null =
		new Array<QuickbooksTaxMappings>();
	default_values: QuickbooksDefaultValues | null = null;
	convert_estimates_to_invoices_on_document_won = true;
	sync_quotes = false;
	sync_payments = false;
	zm_item_type_and_billing_period_to_qbo_account_mappings: ProductTypeToQuickbooksAccountMappingRequest[] =
		new Array<ProductTypeToQuickbooksAccountMappingRequest>();
	zm_payment_mode_to_qbo_payment_method_mappings: ZomentumPaymentModeToQuickbooksPaymentMapping[] =
		[];
	zm_payment_mode_to_qbo_deposit_account_mappings: ZomentumPaymentModeToQuickbooksDepositAccountMapping[] =
		[];
}

export enum QuickbooksProductBillingPeriodForMapping {
	OneTime = "one_time",
	Recurring = "recurring"
}

const QuickbooksProductBillingPeriodForMappingValues = Object.values(
	QuickbooksProductBillingPeriodForMapping
);

export const ProductTypeToBillingPeriodMap: Record<
	Exclude<ProductType, ProductType.Bundle>,
	QuickbooksProductBillingPeriodForMapping[]
> = {
	[ProductType.Hardware]: QuickbooksProductBillingPeriodForMappingValues,
	[ProductType.Software]: QuickbooksProductBillingPeriodForMappingValues,
	[ProductType.Service]: QuickbooksProductBillingPeriodForMappingValues,
	[ProductType.Labor]: QuickbooksProductBillingPeriodForMappingValues,
	[ProductType.Other]: QuickbooksProductBillingPeriodForMappingValues
};

export enum AvailablePaymentMethods {
	ACH = "ach",
	CREDIT_CARD = "credit_card"
}

export class OrderFormCustomFields {
	data: CustomField[] = [
		{
			id: "8df5e7307e82449c82967c759d4cf0a3",
			created: new Date(),
			updated: new Date(),
			deleted: false,
			display_name: "Phone",
			custom_field_type: CustomFieldType.NumberField,
			company_id: "a74d03effb21433ba372a6a5bd7f9dda",
			entity_type: ZomentumEntities.Client,
			allowed_dropdown_values: [],

			creator_user_id: "e47fd2a9acf649b798f6b4c9c4e1e518",

			values: [],
			system_field_id: "client_phone",
			is_editable_field: false,
			is_deletable_field: false,
			field_type: CustomFieldType.NumberField,

			mandatory_field_details: null
		},
		{
			id: "62f5a4cbd1964ffca1dcaee698319994",
			created: new Date(),
			updated: new Date(),
			deleted: false,
			display_name: "Billing Address",
			custom_field_type: CustomFieldType.TextField,
			company_id: "a74d03effb21433ba372a6a5bd7f9dda",
			entity_type: ZomentumEntities.Client,
			allowed_dropdown_values: [],

			creator_user_id: "e47fd2a9acf649b798f6b4c9c4e1e518",

			values: [],
			system_field_id: "client_billing_address",
			is_editable_field: false,
			is_deletable_field: false,
			field_type: CustomFieldType.TextField,

			mandatory_field_details: null
		},
		{
			id: "fe3095011a474f468e1f0e5010c637d3",
			created: new Date(),
			updated: new Date(),
			deleted: false,
			display_name: "Fax",
			custom_field_type: CustomFieldType.TextField,
			company_id: "a74d03effb21433ba372a6a5bd7f9dda",
			entity_type: ZomentumEntities.Client,
			allowed_dropdown_values: [],

			creator_user_id: "e47fd2a9acf649b798f6b4c9c4e1e518",

			values: [],
			system_field_id: "client_fax",
			is_editable_field: false,
			is_deletable_field: false,
			field_type: CustomFieldType.TextField,

			mandatory_field_details: null
		},
		{
			id: "332e6a4a158c4d2ea3a677ef7e5e9a90",
			created: new Date(),
			updated: new Date(),
			deleted: false,
			display_name: "Website",
			custom_field_type: CustomFieldType.TextField,
			company_id: "a74d03effb21433ba372a6a5bd7f9dda",
			entity_type: ZomentumEntities.Client,
			allowed_dropdown_values: [],

			creator_user_id: "e47fd2a9acf649b798f6b4c9c4e1e518",

			values: [],
			system_field_id: "client_website",
			is_editable_field: false,
			is_deletable_field: false,
			field_type: CustomFieldType.TextField,

			mandatory_field_details: null
		},
		{
			id: "85e9c80f16af4d6cb45df9aa1be314c5",
			created: new Date(),
			updated: new Date(),
			deleted: false,
			display_name: "LinkedIn",
			custom_field_type: CustomFieldType.TextField,
			company_id: "a74d03effb21433ba372a6a5bd7f9dda",
			entity_type: ZomentumEntities.Client,
			allowed_dropdown_values: [],

			creator_user_id: "e47fd2a9acf649b798f6b4c9c4e1e518",

			values: [],
			system_field_id: "client_linked_in_url",
			is_editable_field: false,
			is_deletable_field: false,
			field_type: CustomFieldType.TextField,

			mandatory_field_details: null
		},
		{
			id: "77a18a4c39f3464eb46fbf20f6777f28",
			created: new Date(),
			updated: new Date(),
			deleted: false,
			display_name: "Facebook",
			custom_field_type: CustomFieldType.TextField,
			company_id: "a74d03effb21433ba372a6a5bd7f9dda",
			entity_type: ZomentumEntities.Client,
			allowed_dropdown_values: [],

			creator_user_id: "e47fd2a9acf649b798f6b4c9c4e1e518",

			values: [],
			system_field_id: "client_facebook_url",
			is_editable_field: false,
			is_deletable_field: false,
			field_type: CustomFieldType.TextField,

			mandatory_field_details: null
		},
		{
			id: "a2dc3b66168a4de4aa412d6d013ec67e",
			created: new Date(),
			updated: new Date(),
			deleted: false,
			display_name: "Twitter",
			custom_field_type: CustomFieldType.TextField,
			company_id: "a74d03effb21433ba372a6a5bd7f9dda",
			entity_type: ZomentumEntities.Client,
			allowed_dropdown_values: [],

			creator_user_id: "e47fd2a9acf649b798f6b4c9c4e1e518",

			values: [],
			system_field_id: "client_twitter_url",
			is_editable_field: false,
			is_deletable_field: false,
			field_type: CustomFieldType.TextField,

			mandatory_field_details: null
		},
		{
			id: "6bd76cb7621641039ba3419c29981ae1",
			created: new Date(),
			updated: new Date(),
			deleted: false,
			display_name: "Market",
			custom_field_type: CustomFieldType.Dropdown,
			company_id: "a74d03effb21433ba372a6a5bd7f9dda",
			entity_type: ZomentumEntities.Client,
			allowed_dropdown_values: [],

			creator_user_id: "e47fd2a9acf649b798f6b4c9c4e1e518",

			values: [],
			system_field_id: "client_market",
			is_editable_field: false,
			is_deletable_field: false,
			field_type: CustomFieldType.Dropdown,

			mandatory_field_details: null
		},
		{
			id: "05cafc0d96c8404791a40c53a46079d3",
			created: new Date(),
			updated: new Date(),
			deleted: false,
			display_name: "Company Types",
			custom_field_type: CustomFieldType.Dropdown,
			company_id: "a74d03effb21433ba372a6a5bd7f9dda",
			entity_type: ZomentumEntities.Client,
			allowed_dropdown_values: [],

			creator_user_id: "e47fd2a9acf649b798f6b4c9c4e1e518",

			values: [],
			system_field_id: "client_company_types",
			is_editable_field: false,
			is_deletable_field: false,
			field_type: CustomFieldType.Dropdown,

			mandatory_field_details: null
		},
		{
			id: "cca0fbe8c142471084171a761c4255ba",
			created: new Date(),
			updated: new Date(),
			deleted: false,
			display_name: "Source",
			custom_field_type: CustomFieldType.TextField,
			company_id: "a74d03effb21433ba372a6a5bd7f9dda",
			entity_type: ZomentumEntities.Client,
			allowed_dropdown_values: [],

			creator_user_id: "e47fd2a9acf649b798f6b4c9c4e1e518",

			values: [],
			system_field_id: "client_source",
			is_editable_field: false,
			is_deletable_field: false,
			field_type: CustomFieldType.TextField,

			mandatory_field_details: null
		},
		{
			id: "4bb0f775c34a469896fe2e118f69e591",
			created: new Date(),
			updated: new Date(),
			deleted: false,
			display_name: "Campaign",
			custom_field_type: CustomFieldType.TextField,
			company_id: "a74d03effb21433ba372a6a5bd7f9dda",
			entity_type: ZomentumEntities.Client,
			allowed_dropdown_values: [],

			creator_user_id: "e47fd2a9acf649b798f6b4c9c4e1e518",

			values: [],
			system_field_id: "client_campaign",
			is_editable_field: false,
			is_deletable_field: false,
			field_type: CustomFieldType.TextField,

			mandatory_field_details: null
		},
		{
			id: "3d71e5d9a8104e12a5e4c029d21a9494",
			created: new Date(),
			updated: new Date(),
			deleted: false,
			display_name: "Date Acquired",
			custom_field_type: "date_field",
			company_id: "a74d03effb21433ba372a6a5bd7f9dda",
			entity_type: ZomentumEntities.Client,
			allowed_dropdown_values: [],

			creator_user_id: "e47fd2a9acf649b798f6b4c9c4e1e518",

			values: [],
			system_field_id: "client_date_acquired",
			is_editable_field: false,
			is_deletable_field: false,
			field_type: CustomFieldType.DateField,

			mandatory_field_details: null
		}
	];
}

export interface HaloPSAIntegration {
	connected_user: string;
	sync_option: string;
	is_initial_sync_pending: boolean;
	block_negative_quantity?: boolean | null;
	block_zero_quantity?: boolean | null;
}

export interface HaloPSAConnectFormValue {
	auth_server: string | null;
	resource_server: string | null;
	tenant: string | null;
	client_id: string | null;
	client_secret: string | null;
}

export interface Supplier {
	id: string;
	company_id: string;
	created: Date;
	updated: Date;
	deleted: boolean;
	name: string;
	user_id: string;
	address?: Address;
	email?: string;
	has_drop_ship: boolean;
	is_system_generated: boolean;
	has_electronic_po: boolean;
	creator_user: UserData | null;
}

export class SupplierCreateRequest {
	name = "";
	email?: string | null;
	address?: AddressValues;
	has_drop_ship = false;

	constructor(
		name: string,
		hasDropShip: boolean,
		email?: string | null,
		address?: AddressValues
	) {
		this.name = name;
		this.has_drop_ship = hasDropShip;
		this.email = !!email?.length ? email : null;
		this.address = undefined;

		if (address && !!Object.keys(address).length) {
			this.address = address;
		}
	}
}

export class SupplierUpdateRequest {
	name = "";
	email?: string | null;
	address?: AddressValues;
	has_drop_ship = false;

	constructor(
		name: string,
		hasDropShip: boolean,
		email?: string | null,
		address?: AddressValues
	) {
		this.name = name;
		this.has_drop_ship = hasDropShip;
		this.address = address;
		this.email = !!email?.length ? email : null;
	}
}

export interface SupplierCount {
	all?: number;
}
export interface SupplierResponse {
	data: Supplier[];
	filtered_count_map: SupplierCount;
}

export interface SupplierRequest {
	limit?: number;
	keywords?: string | null;
	selected_id?: string | null;
}

export interface BulkActionMetadataResponse {
	entity_type: ZomentumEntities;
	failures: string[];
	ids_matched: string[];
}

export interface BulkActionResponse {
	company_id: string;
	created: Date;
	deleted: boolean;
	id: string;
	operation_completed_at: Date | null;
	operation_started_at: Date | null;
	operation_status: string;
	updated: Date;
	user_id: string;
	vendor_company_id: string;
	metadata: BulkActionMetadataResponse;
}

export enum FormKeys {
	AddEditPipeline = "add_edit_pipeline",
	AddEditTaxCategory = "add_edit_tax_category",
	AddEditTaxRegion = "add_edit_tax_region",
	AddEditUser = "add_edit_user",
	AddDomain = "add_domain",
	AddEmail = "add_email",
	AddEditSupplier = "add_edit_supplier",
	AddEditNote = "add_edit_note",
	AddEditCallLog = "add_edit_call_log",
	AddEditEmailLog = "add_edit_email_log",
	AddEditMeeting = "add_edit_meeting",
	AddEditTask = "add_edit_task",
	AddEditCloneProduct = "add_edit_clone_product",
	EditClient = "edit_client",
	AddClient = "add_client",
	EditCustomField = "edit_custom_field",
	AddCustomField = "add_custom_field",
	CreateDocument = "create_document",
	EditDocument = "edit_document",
	AddEtilizeProduct = "add_etilize_product",
	SendEmail = "send_email",
	EditOpportunity = "edit_opportunity",
	AddOpportunity = "add_opportunity",
	EditTemplate = "edit_template",
	AddOverrideTemplate = "add_override_template"
}

export type IsFormEdited = Record<string, boolean>;

export enum AmazonMarketPlaceURL {
	US = "https://www.amazon.com",
	CA = "https://www.amazon.ca",
	ES = "https://www.amazon.es",
	UK = "https://www.amazon.co.uk",
	FR = "https://www.amazon.fr",
	DE = "https://www.amazon.de",
	IT = "https://www.amazon.it",
	JP = "https://www.amazon.co.jp"
}

export interface AmazonRegion {
	readonly display_name: string;
	readonly region: string;
	readonly locales: AmazonLocaleValues[];
}

export interface AmazonLocaleValues {
	readonly display_name: string;
	readonly locale: string;
}

export interface AmazonMetadataResponse {
	readonly locales: AmazonLocaleValues[];
	readonly regions: AmazonRegion[];
}

export interface AmazonIntegration {
	connected_user: string;
	locale: string;
	region: string;
}

export enum AmazonLocale {
	US = "en-US",
	UK = "en-GB"
}

export interface AirtableConnectRequest {
	pat: string;
}

export interface AirtableFieldSchema {
	id: string;
	name: string;
	type: string;
}

export interface AirtableTableSchema {
	id: string;
	name: string;
	primary_field_id: string;
	fields: AirtableFieldSchema[];
}

export interface AirtableMetadataBase {
	id: string;
	name: string;
	permission_level: string;
	tables: AirtableTableSchema[];
}

export interface AirtableMetadataResponse {
	bases: AirtableMetadataBase[];
}

export interface AirtableFieldRefView {
	field_id: string;
	field_name: string;
}

export interface AirtableFieldMappingView {
	zomentum_field: string;
	airtable_field_id: string;
}

export interface AirtableParentLinkMappingView {
	parent: string;
	airtable_field_id: string;
}

export interface AirtableEntityMappingView {
	entity_type: string;
	table_id: string;
	external_id_field: AirtableFieldRefView;
	field_mappings: AirtableFieldMappingView[];
	parent_link_mappings: AirtableParentLinkMappingView[];
}

export interface AirtableIntegrationConfigView {
	connected: boolean;
	is_enabled?: boolean;
	connected_user?: string;
	credentials_created?: string;
	credentials_updated?: string;
	base_id?: string;
	last_synced_at?: string;
	consecutive_incomplete_runs?: number;
	entity_mappings: AirtableEntityMappingView[];
}

export interface AirtableFieldMappingPayload {
	zomentum_field: string;
	airtable_field_id: string;
}

export interface AirtableParentLinkMappingPayload {
	parent: string;
	airtable_field_id: string;
}

export interface AirtableEntityMappingPayload {
	entity_type: string;
	table_id: string;
	external_id_field_id: string;
	field_mappings: AirtableFieldMappingPayload[];
	parent_link_mappings: AirtableParentLinkMappingPayload[];
}

export interface AirtableBaseSelectionRequest {
	base_id: string;
}

export interface AirtableEntityMappingUpdateRequest {
	entity_mapping: AirtableEntityMappingPayload;
}

export type AirtableMappingUpdateRequest =
	| AirtableBaseSelectionRequest
	| AirtableEntityMappingUpdateRequest;

export enum AirtableEntityTypeKey {
	ClientCompany = "client_company",
	ClientUser = "client_user",
	Opportunity = "opportunity",
	Document = "document"
}

export enum AirtableZomentumClientField {
	Name = "client_name",
	Website = "client_website",
	CompanyNumber = "client_company_number",
	BillingStreet = "client_billing_street",
	BillingCity = "client_billing_city",
	BillingState = "client_billing_state",
	BillingZip = "client_billing_zip",
	BillingCountry = "client_billing_country",
	BillingPhone = "client_billing_phone",
	CompanyTypes = "client_company_types",
	Market = "client_market",
	DateAcquired = "client_date_acquired",
	Source = "client_source",
	FacebookUrl = "client_facebook_url",
	LinkedInUrl = "client_linkedin_url",
	TwitterUrl = "client_twitter_url",
	CreatedAt = "client_created_at",
	UpdatedAt = "client_updated_at",
	OwnerName = "client_owner_name",
	OwnerEmail = "client_owner_email",
	CompanySubdomain = "company_subdomain",
	OpenInZomentum = "open_in_zomentum"
}

export enum AirtableZomentumContactField {
	FirstName = "contact_first_name",
	LastName = "contact_last_name",
	Email = "contact_email",
	AdditionalEmails = "contact_additional_emails",
	Phone = "contact_phone",
	Mobile = "contact_mobile",
	JobTitle = "contact_job_title",
	Department = "contact_department",
	ContactType = "contact_contact_type",
	CreatedAt = "contact_created_at",
	UpdatedAt = "contact_updated_at",
	CompanySubdomain = "company_subdomain",
	OpenInZomentum = "open_in_zomentum"
}

export enum AirtableZomentumOpportunityField {
	Name = "opportunity_name",
	EstimatedValue = "opportunity_estimated_value",
	Currency = "opportunity_currency",
	CloseProbability = "opportunity_close_probability",
	CloseDate = "opportunity_close_date",
	EstimatedCloseDate = "opportunity_estimated_close_date",
	ClientPoNumber = "opportunity_client_po_number",
	Source = "opportunity_source",
	Campaign = "opportunity_campaign",
	WonReason = "opportunity_won_reason",
	LossReason = "opportunity_loss_reason",
	Margin = "opportunity_margin",
	MarginPercentage = "opportunity_margin_percentage",
	TypeName = "opportunity_type_name",
	OwnerName = "opportunity_owner_name",
	OwnerEmail = "opportunity_owner_email",
	PipelineName = "opportunity_pipeline_name",
	StageName = "opportunity_stage_name",
	CreatedAt = "opportunity_created_at",
	UpdatedAt = "opportunity_updated_at",
	CompanySubdomain = "company_subdomain",
	OpenInZomentum = "open_in_zomentum"
}

export enum AirtableZomentumDocumentField {
	Name = "document_name",
	Number = "document_number",
	Type = "document_type",
	Status = "document_status",
	SentDate = "document_sent_date",
	Description = "document_description",
	Margin = "document_margin",
	MarginPercentage = "document_margin_percentage",
	MarkupMarginPercentage = "document_markup_margin_percentage",
	Discount = "document_discount",
	TotalTax = "document_total_tax",
	PoNumber = "document_po_number",
	Currency = "document_currency",
	GrandTotal = "document_grand_total",
	GrandCost = "document_grand_cost",
	RevenueOneTime = "document_revenue_one_time",
	RevenueWeekly = "document_revenue_weekly",
	RevenueMonthly = "document_revenue_monthly",
	RevenueQuarterly = "document_revenue_quarterly",
	RevenueSemiAnnually = "document_revenue_semi_annually",
	RevenueAnnual = "document_revenue_annual",
	TotalRevenue = "document_total_revenue",
	TotalCost = "document_total_cost",
	OwnerName = "document_owner_name",
	OwnerEmail = "document_owner_email",
	CreatedAt = "document_created_at",
	UpdatedAt = "document_updated_at",
	CompanySubdomain = "company_subdomain",
	OpenInZomentum = "open_in_zomentum"
}

export enum AirtableParentKind {
	Client = "client",
	Contact = "contact",
	Opportunity = "opportunity"
}
